export default {
    onboarding: {
        title: 'Finish setting up your Professional account',
        subtitle:
            'Complete these steps to start managing your brand and events. You can pick up where you left off any time.',
        step_counter: 'Step {current} of {total}',
        steps: {
            account_created: {
                label: 'Create your account',
                description: 'Your account is ready — welcome to LatinMap.',
                cta: '',
            },
            email_verified: {
                label: 'Verify your email address',
                description: 'Confirm your email so we can reach you about your events and account.',
                cta: '',
            },
            sms_verified: {
                label: 'Verify your phone number',
                description: 'A quick SMS verification unlocks publishing and protects your account.',
                cta: 'Verify',
            },
            brand_added: {
                label: 'Claim or create your brand',
                description: 'Set up your organization, dance school or professional profile page.',
                cta: 'Add brand',
            },
            event_added: {
                label: 'Add your first event',
                description: 'Publish an event so dancers can find you on LatinMap.',
                needs_brand:
                    'You need a brand before you can add an event. Create your brand first, then you can publish events.',
                cta: 'Add event',
                cta_needs_brand: 'Create a brand first',
            },
            dashboard_discovered: {
                label: 'Explore your Professional Dashboard',
                description: 'Manage your brand pages, events, ticket links and more from one place.',
                cta: 'Open dashboard',
            },
        },
    },
    cookie_consent: {
        title: 'We value your privacy',
        intro: 'We use cookies to keep LatinMap working, to understand how it is used, and to improve it. You choose which cookies we may use. Necessary cookies are always on.',
        always_on: 'Always on',
        accept_all: 'Accept all',
        reject_all: 'Reject all',
        customize: 'Customize',
        save: 'Save choices',
        categories: {
            necessary: {
                title: 'Necessary',
                description:
                    'Required for the site to function, such as your login session, security and language. These cannot be switched off.',
            },
            preferences: {
                title: 'Preferences',
                description:
                    'Remember choices you make, such as your preferred language or region, for a more personal experience.',
            },
            statistics: {
                title: 'Statistics',
                description:
                    'Help us understand how visitors use LatinMap by collecting anonymous usage data (e.g. Google Analytics).',
            },
            marketing: {
                title: 'Marketing',
                description:
                    'Used to measure campaigns and show you more relevant content across platforms (e.g. Meta Pixel, Google Ads).',
            },
        },
    },
    admin: {
        title: 'Admin',
        quick_edit: {
            event: 'Edit event',
            brand: 'Edit brand',
        },
        superadmin: {
            title: 'Super admin',
            edit: 'Edit',
        },
        invite: {
            manage: 'Invite Ambassadors of the community',
            resend: 'Resend',
            resend_success: 'Invitation has been resent',
        },
        events: {
            edit: {
                singular: 'Edit event',
                plural: 'Edit events',
            },
            overview: 'Events',
        },
        organizations: {
            edit: {
                singular: 'Edit organization & brand',
                plural: 'Edit organizations & brands',
            },
            overview: 'Organizations & brands',
        },
        pages: {
            edit: {
                singular: 'Edit page',
                plural: 'Edit pages',
            },
            update: {
                success: 'Page updated',
                error: 'An error occurred while updating the page.',
                singular: 'Update Page',
                title: 'Title',
                content: 'Content',
                update_success: 'Page updated successfully',
            },
            overview: 'Pages',
            title: 'Title',
            content: 'Content',
        },
        prices: {
            update: {
                success: 'Prices updated',
                singular: 'Update Prices',
                title: 'Title',
                content: 'Content',
                update_success: 'Prices updated successfully',
            },
            success: 'Purchase successful',
        },
        users: {
            edit: {
                singular: 'Edit user',
                plural: 'Edit users',
            },
            overview: 'Users',
            active: 'Active',
            passive: 'Passive',
            impersonate: {
                button: 'Impersonate',
                loading: 'Impersonating...',
                error: 'Could not impersonate this user.',
            },
        },
        // LA-194: the links Admin hands out so an organiser can submit
        // an event without having an account.
        submission_links: {
            title: 'Submission links',
            intro: 'Links that let organisers submit an event without an account.',
            new: 'New link',
            new_hint: 'Create a link to hand to an organiser over WhatsApp or email.',
            create: 'Create link',
            created: 'Link created',
            search: 'Search',
            copy: 'Copy link',
            copied: 'Copied',
            base_url: 'Every link starts with:',
            copy_base: 'Copy the start of the link',
            withdraw: 'Withdraw',
            reactivate: 'Reactivate',
            no_brand: 'No brand yet',
            empty: 'No links yet.',
            failed: 'Something went wrong.',
            has_submissions: 'Submissions came in through this link; withdraw it instead of deleting.',
            delete_confirm: 'Delete link {code}?',
            fields: {
                code: 'Code',
                label: 'Description',
                label_placeholder: 'e.g. Salsa Nights Rotterdam',
                brand: 'Brand',
                brand_placeholder: 'Search a brand',
                brand_hint: 'Optional. Decides which brand a submission belongs to.',
                state: 'State',
                submissions: 'Submissions',
                expires_at: 'Expires on',
                max_submissions: 'Max submissions',
                contact_name: 'Contact',
                contact_email: 'Email address',
            },
            states: {
                all: 'All',
                active: 'Active',
                expired: 'Expired',
                withdrawn: 'Withdrawn',
                full: 'Full',
            },
        },
        scraped_concepts: {
            title: 'Scraped Concepts',
            overview: 'Concepts Overview',
            edit: { title: 'Edit Concept' },
            columns: {
                source: 'Source',
                name: 'Name',
                dates: 'Dates',
                location: 'Location',
                organization: 'Organization',
                status: 'Status',
                brand_match: 'Brand',
                actions: 'Actions',
            },
            filters: {
                name: 'Event name',
                source: 'Source',
                status: 'Status',
                city: 'City',
                date_from: 'Date from',
                date_to: 'Date to',
                all: 'All',
                source_organization: 'Scraped organizer',
                brand_status: 'Brand status',
            },

            brand_status: {
                unchecked: 'Not checked yet',
                unmatched: 'Not on LatinMap yet',
                unmanaged: 'Exists, unclaimed',
                review: 'Check this one',
                managed: 'Claimed',
            },
            match_signal: {
                email: 'matched on email',
                domain: 'matched on website',
                name: 'matched on name',
                name_city: 'matched on name + city',
                fuzzy: 'similar name — verify',
            },
            unlink_brand: 'Unlink brand',
            organization_unlinked: 'Brand unlinked',
            unlink_confirm: {
                title: 'Unlink brand?',
                submit: 'Yes, unlink',
                body: 'Remove the link between "{event}" and the brand "{brand}"?',
                note: 'The concept stays in the list and can be linked to another brand. Nothing is deleted.',
            },
            link_confirm: {
                title: 'Link brand?',
                submit: 'Yes, link',
                body: 'Link "{event}" to the brand "{brand}"? The event still has to be approved separately.',
                signal: 'Found:',
                warning_managed:
                    'This brand is actively managed by a Professional. Linking a scraped event may clash with their own work.',
                warning_review: 'This match is not certain. Check that it really is the same brand before linking.',
            },
            link_match: 'Link this brand',
            refresh_matches: 'Refresh matches',
            refresh_matches_help: 'Recalculate which existing brands the scraped organisers belong to',
            matches_refreshed: 'Matches refreshed: {certain} certain, {possible} to check',
            bulk: {
                selected: '{count} selected',
                link_matched: 'Link matched brand',
                approve: 'Approve',
                reject: 'Reject',
                clear: 'Clear selection',
                running: 'Working...',
                select_all: 'Select all on this page',
                select_one: 'Select {name}',
                nothing_to_link: 'Nothing in the selection has a certain match to link',
                nothing_to_unlink: 'Nothing in the selection has a brand linked',
                unlink: 'Unlink',
                result: '{succeeded} processed, {failed} skipped',
                confirm: {
                    title: 'Confirm bulk action',
                    submit: 'Yes, continue',
                    body_approve:
                        'Publish {count} concept(s)? Concepts without a confirmed or certain brand are skipped.',
                    body_reject: 'Reject {count} concept(s)?',
                    body_unlink: 'Remove the brand link from {count} concept(s)?',
                    unlink_skipped: '{skipped} of the selection are skipped: they have no brand linked.',
                    body_assign:
                        'Link {count} concept(s) to their matched brand? The events still have to be approved separately.',
                    assign_skipped: '{skipped} of the selection are skipped: already linked, or no certain match.',
                    and_more: 'and {count} more',
                    assign_managed:
                        '{count} of these brands are actively managed by a Professional. Linking may clash with their own work.',
                },
            },
            status_labels: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' },
            no_organization: 'No organization assigned',
            submitted_by: 'Submitted by',
            no_results: 'No concepts found',
            assign_first: 'Please assign an organization first',
            assign_organization: 'Assign Organization',
            search_organization: 'Search organization by name',
            current_organization: 'Currently assigned',
            organization_assigned: 'Organization assigned successfully',
            source_organization_suggestion: 'Scraped organizer',
            source_dance_styles: 'Scraped dance/music styles',
            managed_brand: 'Managed',
            managed_by: 'Managed by',
            active_warning: 'Actively managed brand — do not link scraped events',
            active_warning_short: 'Active account',
            active_confirm: {
                title: 'Actively managed brand',
                body: "This brand is actively managed by an account with a verified phone number ({email}). Linking scraped events may conflict with the owner's work. Are you sure you want to link this concept?",
                submit: 'Link anyway',
            },
            show_event: 'Show event',
            approve: 'Approve',
            reject: 'Reject',
            approved_success: 'Concept approved and event created successfully',
            rejected_success: 'Concept rejected',
            view_event: 'View created event',
            posted_by_label: {
                title: 'Show "Posted by LatinMap" label',
                automatic: 'Automatic (only when LatinMap is not the brand)',
                yes: 'Yes, show the label',
                no: 'No, do not show it',
                help: 'Shown on the public event page. The brand removes it themselves by editing the event.',
            },
            open_event_page: 'Open the event page',
            submitted_organizer: 'Organizer named by the submitter',
            approve_error: 'Failed to approve concept',
            save_success: 'Concept saved successfully',
            scraped_at: 'Scraped at',
            view_source: 'View source',
            photos: 'Photos',
            admin_notes: 'Admin notes',
            reject_confirm: {
                title: 'Reject Concept',
                body: 'Are you sure you want to reject this concept?',
                submit: 'Reject',
            },
            search_btn: 'Search',
            back: 'Back',
            missing_fields: 'Please fill in the required fields',
            scraping: 'Scraping',
            fetching_events: 'Fetching events from source',
            scrape_result: 'Fetched {fetched}, created {created}, skipped {skipped}, errors {errors}',
            scrape_confirm: {
                title: 'Start Scraping',
                body: 'Are you sure you want to scrape events from {source}?',
                submit: 'Start Scraping',
                force_update: 'Update existing events',
                force_update_help:
                    'When enabled, existing events will be updated with the latest data instead of being skipped.',
            },
            cleanup: {
                button: 'Cleanup past',
                title: 'Cleanup Past Events',
                body: 'Are you sure you want to delete all scraped concepts with a start date in the past? This cannot be undone.',
                submit: 'Delete past events',
                success: '{count} past events deleted',
            },
        },
        report: {
            details: 'Details:',
            reported: 'Reported by:',
            suspended_at: 'Suspended at:',
            email: 'Email:',
            phonenumber: 'Phonenumber:',
            message: 'Message:',
            singular: 'Report',
            plural: 'Reports',
            reportables: {
                event: 'Events',
                organization: 'Organizations',
            },
            status: {
                submitted: 'Submitted',
                under_review: 'Under review',
                resolved: 'Resolved',
                no_issue: 'No issue',
            },
            reportables_by_type: {
                event: 'Event',
                organisation: 'Organizations & brands',
                user: 'User',
                created_by: 'Created by',
                report_by: 'Report by',
                ip_adress: 'IP-adress',
                reason: 'Reason',
                description: 'Description',
                empty: 'empty',
            },
            no_reports: 'No reports found',
        },
        lead: {
            singular: 'Lead',
            plural: {
                event: 'Event lead',
                organization: 'Brand lead',
            },
            user_type: 'User Type',
            user_email: 'User Email',
        },
        block_user: {
            plural: 'Block users',

            title: 'BLOCKED',
            description: "Your account has been blocked, you don't have access to the platform anymore.",
        },
        setting: {
            plural: 'Settings',
            reset: {
                warning: {
                    title: 'Reset all the settings in the admin.',
                    body: 'Are you sure you want to reset all the settings? All settings will return to their original value and this action cannot be undone.',
                },
            },
        },
        permissions: {
            plural: 'Permissions',
            levels: {
                'level-1': 'Super admin',
                'level-2': 'Admin',
                'level-3': 'Ambassador',
                'level-4': 'Premium member',
                'level-5': 'Professional member',
                'level-6': 'Member',
            },
            no_users_with_role: 'There are no users with this role level.',
            rights: {
                'create-organization-on-behalf-of-other-user': 'Create organization on behalf of other user',
                'latinmap-verify': 'Grant LatinMap verified status',
                invite: {
                    'level-3': 'Invite Ambassadors of the community',
                },
                reports: {
                    view: 'View reports',
                    delete: {
                        any: 'Delete any report',
                    },
                    update: 'Update report status',
                    export: 'Export reports',
                },
                events: {
                    create: 'Create events',
                    view: 'View events',
                    update: {
                        any: 'Update any event',
                        own: 'Update own events',
                    },
                    delete: {
                        any: 'Delete any event',
                        own: 'Delete own events',
                    },
                    publish: 'Publish events',
                    unpublish: 'Unpublish events',
                    ownership: {
                        own: 'Change ownership of own events',
                        any: 'Change ownership of any event',
                    },
                    ticketshop: {
                        enable: 'Enable ticketshop',
                    },
                },
                organisations: {
                    create: 'Create organizations & brands',
                    update: {
                        any: 'Update any organizations & brands',
                        own: 'Update own organizations & brands',
                    },
                    delete: {
                        any: 'Delete any organizations & brands',
                        own: 'Delete own organizations & brands',
                    },
                    ownership: {
                        own: 'Change ownership of own organizations & brands',
                        any: 'Change ownership of any organizations & brands',
                    },
                },
                users: {
                    edit: 'Edit users',
                },
                leads: {
                    view: 'View leads',
                    delete: 'Delete leads',
                },
                transfer_requests: {
                    view: 'View transfer requests',
                    approve: 'Approve transfer requests',
                },
            },
        },
        dance_styles: {
            title: 'Dance styles',
            singular: 'Dance style',
            create: {
                singular: 'Create dance style',
            },
            edit: {
                singular: 'Edit dance style',
                plural: 'Edit dance styles',
            },
            delete: {
                title: 'Delete dance style?',
                singular: 'Delete',
                cancel: 'Cancel',
                confirmation:
                    'This dance style has sub dance styles. Are you sure you want to delete this dance style and all its sub dance styles?',
                affected:
                    'This dance style is used in {event_count} events and {organization_count} organizations. Are you sure you want to delete this dance style?',
                label: 'Delete dance style',
                placeholder: 'Type "{keyword}" here and submit to remove this dance style.',
            },
            sub_dance_style: 'Sub dance style',
            is_favorite: 'Is favorite',
        },
        music_styles: {
            title: 'Music styles',
            singular: 'Music style',
            create: {
                singular: 'Create music style',
            },
            edit: {
                singular: 'Edit music style',
                plural: 'Edit music styles',
            },
            delete: {
                title: 'Delete music style?',
                singular: 'Delete',
                cancel: 'Cancel',
                confirmation:
                    'This music style has sub music styles. Are you sure you want to delete this dance style and all its sub dance styles?',
                affected:
                    'This music style is used in {event_count} events and {organization_count} organizations. Are you sure you want to delete this music style?',
                label: 'Delete music style',
                placeholder: 'Type "{keyword}" here and submit to remove this music style.',
                substyles:
                    'This music style has sub-music styles. Are you sure that you want to delete this music style?',
            },
            sub_dance_style: 'Sub Dance style',
            sub_music_style: 'Sub Music style',
            is_favorite: 'Is favorite',
        },
        featuredEvent: 'Featured event',
        event_types: {
            edit: {
                plural: 'Edit event types',
            },
        },
        brand_types: {
            edit: {
                plural: 'Edit brand types',
            },
        },
        prof_financial_title: 'Financial center',
        modoration: {
            title: 'Moderation',
        },
        official_brands: 'Offical brands',
        export: {
            overview: {
                title: 'Export & Transfers',
                subtitle: 'Export all organisations, a transfer link will be generated for each organization.',
            },
            create: 'Create export',
            copied_link: 'Link copied',
            take_ownership: 'Take ownership',
            validate_phone:
                'In order to take ownership of this organization you first need to validate your phone number, this can be done below here:',
            require_login:
                'In order to take ownership of this organization you first need to log in or create an account, this can be done below here:',
            to_login: 'To login',
            messages: {
                opened: 'Your transfer link has been successfully activated, you have 30 days to complete the process.',
                continue: 'Your transfer link has been successfully reopened, please finish the transfer process.',
                no_key: 'No key has been provided, this can happen if you refreshed the page, you should open the original link again.',
                not_exists: 'There currently is no transfer link for this organization.',
                unauthorized: 'You are not authorized to accept this organization transfer.',
                expired: 'This link has expired. Contact LatinMap to receive a new transfer link.',
                already_completed: 'This link has already been completed and can no longer be used.',
                transferred: 'The organization has been successfully transferred.',
            },
            warning: {
                title: 'Delete transfer link',
                body: 'Are you sure you want to delete this transfer link?',
            },
            status: {
                no_action: 'Not transferred',
                opened: 'Opened',
                completed: 'Transferred',
                expired: 'Expired',
            },
        },
        duplications: 'Duplications',
        suspend_toggle: {
            warning: {
                title: 'Toggle suspension',
                body: 'Are you sure you want to change the suspension of this?',
            },
        },
        take_offline: {
            warning: {
                title: 'Take offline',
                body: 'Are you sure you want to take this offline, it will no longer be visible for users.',
            },
        },
        bring_online: {
            warning: {
                title: 'Bring online',
                body: 'Are you sure you want to bring this online, it will be visible to users.',
            },
        },
        'carousel-image': {
            plural: 'Carousel images',
            overview: {
                title: 'Carousel images',
                subtitle: 'Manage the carousel images on the homepage.',
            },
            create: {
                singular: 'Create carousel image',
                success: 'Carousel image created successfully',
                error: 'Error creating carousel image',
            },
            delete: {
                success: 'Carousel image deleted successfully',
                error: 'Error deleting carousel image',
            },
            image: 'Image',
            visibility: 'Seconds visible',
            title: 'Title',
            order_by: 'Order by',
            warning: {
                body: 'Are you sure you want to delete this carousel image? This action cannot be undone.',
                title: 'Delete carousel image',
            },
        },
        'beta-feature': {
            plural: 'Beta features',
            overview: {
                title: 'Beta features',
                subtitle: 'Manage the beta features on the homepage.',
                success: 'Beta features stored successfully',
                error: 'Error storing or fetching beta features',
            },
            key: 'Key',
            name: 'Name',
            description: 'Description',
            visible_to_admins: 'Visible to admins',
            visible_to_users: 'Visible to users',
        },
    },
    nav_headers: {
        agenda: {
            label: 'Latin Agenda',
            options: {
                all_events: 'All Events',
                latin_festivals_and_congresses: 'Latin Festivals & Congresses',
                latin_parties_and_socials: 'Latin Parties & Socials',
            },
        },
        dance_schools: {
            label: 'Latin Dance Schools',
            options: {
                dance_schools: 'Dance Schools',
                try_outs_and_start_courses: 'Try Outs & Start Courses',
                workshops_and_bootcamps: 'Workshops & Bootcamps',
                about_the_latin_dance_scene: 'About the Latin Dance Scene',
            },
        },
        community: {
            label: 'Latin Community',
            options: {
                teachers: 'Teachers',
                show_performers_and_teams: 'Show Performers & Teams',
                djs: "DJ's",
                live_bands: 'Live Bands',
                media_photo_video: 'Media, Photo, Video',
                mcs: "MC's",
            },
        },
        dancewear_and_shoes: {
            label: 'Latin Dancewear & Shoes',
            options: {
                dancewear_and_shoes: 'Dancewear & Shoes',
            },
        },
        for_professionals: {
            label: 'For Professionals',
            options: {
                create_a_brand: 'How to Create a Brand',
                create_an_event: 'How to Create an Event',
                promote_your_event: 'How to Promote Your Event',
                professional_account_verification: 'Professional Account Verification',
            },
        },
        join: {
            label: 'Join LatinMap',
            options: {
                create_a_brand_or_organization: 'Create a Brand or Organization',
                become_an_ambassador: 'Become an Ambassador',
            },
        },
        manage_and_promote: {
            label: 'Manage & Promote',
            options: {
                my_events: 'My Events',
                promote_your_event: 'Promote Your Event',
            },
        },
    },
    impersonation: {
        banner: 'You are viewing as {name}',
        stop: 'Stop impersonating',
    },
    menu_headers: {
        general_title: 'General',
        orders_title: 'My orders',
        settings_title: 'Settings',
        professional_title: 'Professional',
        prof_financial_title: 'My financial center',
        special_access_title: 'Special access',
        community_dashboard_title: 'My ambassador of the Community dashboard',
        session_title: 'Session',
    },
    account: {
        organization_settings_title: 'Organization Settings',
        selected_organization: 'Selected organization',
        my_events: 'My events',
        role: 'Role',
        dashboard: 'Dashboard',
        default_filters: 'My searches',
        login: 'Login',
        logout: 'Logout',
        sign_in: 'Sign in',
        sign_up: 'Sign up',
        email_address: 'E-mail address',
        password: 'Password',
        password_repeat: 'Repeat password',
        password_reset: 'Password reset',
        password_forgot: 'Password forgotten',
        reset_password: 'Reset password',
        current_password: 'Current password',
        change_password: 'Change password',
        new_password: 'New password',
        password_reset_success: 'Password reset successful',
        forgot_password: 'Forgot password',
        not_a_member: 'Not a member',
        i_already_have_an_account: 'I already have an account',
        i_accept_the: 'I accept the',
        i_am: {
            label: 'I am a…',
            dancer: 'Dancer',
            professional: 'Professional (organizer, dance school, DJ, artist…)',
        },
        newsletter_optin: 'I want to receive the LatinMap newsletter',
        terms_and_conditions: 'terms and conditions',
        first_name: 'First name',
        last_name: 'Last Name',
        postal_code: 'Postal Code',
        street_number: 'Street number',
        city: 'City',
        country: 'Country',
        preferred_language: 'Preferred language',
        date_of_birth: 'Date of birth',
        create: 'Create account',
        my_account: 'My account',
        my_professional_dashboard: 'My professional dashboard',
        my_organisations: 'My organizations & brands',
        my_organisations_description: 'Manage your organizations & brands',
        singular: 'account',
        phone: 'Phone number',
        phone_verification_token: 'Phone verification token',
        name: 'Name',
        user_name: 'Username',
        resend_verification: 'Resend verification',
        update_error: 'Updating your account failed. Please try again or contact support.',
        myLatinmap: 'My LatinMap',
        dancerprofile: 'My Dancers profile',
        orders: 'My Orders',
        phone_verification: 'Phone verification',
        submitted_leads: 'My Submitted Leads',
        settings: 'Settings',
        switch: 'Switch to brand/organization',
        ambassador_dashboard: 'Your ambassador dashboard',
        beta_features: 'Beta features',
        gender: {
            singular: 'Gender',
            male: 'Male',
            female: 'Female',
            other: 'Other',
        },
        profile: {
            title: 'Profile',
            description: 'Manage your profile information, change your password and more.',
        },
        remove: {
            label: 'Remove account',
            placeholder: 'Type "delete" here and submit to remove your account.',
            admin_placeholder: 'Type "delete" here and submit to remove this account.',
            safeword: 'delete',
            description:
                'This will remove your account and all associated data. This will not remove Organisations and events created. This action is irreversible.',
        },
        favorites: {
            title: 'Favorites',
            description: 'Your favorite events, dance schools etc.',
            no_favorites: 'You have no favorites in this category yet',
            plurals: {
                event: 'Events',
                dancer: 'Dancers',
                dj: "DJ's",
                media: 'Media',
                brand: 'Dance Shoes, Clothing, Store',
                school: 'Dance schools',
                venue: 'Venues',
                club: 'Club',
                teacher: 'Teacher',
                show_performers_and_teams: 'Show Performers & Teams',
                performer: 'Performer',
                mc: 'Mc',
                latin_music: 'Latin music',
                event_organizer: 'Event organizer',
                organization: 'Organizations & brands',
            },
        },
        likes: {
            title: 'Likes',
            description: 'Your liked events, dance schools etc.',
            no_likes: 'You have no likes in this category yet',
            plurals: {
                event: 'Events',
                dancer: 'Dancers',
                dj: "DJ's",
                media: 'Media (photo, video, video editing)',
                brand: 'Dance Shoes, Clothing, Store',
                school: 'Dance schools',
                venue: 'Venues',
                club: 'Club',
                teacher: 'Teacher',
                show_performers_and_teams: 'Show Performers & Teams',
                performer: 'Performer',
                mc: 'Mc',
                latin_music: 'Latin music',
                event_organizer: 'Event organizer',
                organization: 'Organizations & brands',
            },
        },
        verification: {
            phone: {
                description:
                    "Phone verification is needed to ensure the security and authenticity of users accessing the 'professional' dashboard. It works by sending a verification code to your phone, which you must enter to confirm your identity.",
                not_verified: 'Your phone number is not verified.',
                send_verification_text: 'Send verification text',
                sent: 'Verification text sent',
                verified: 'Your phone number is verified.',
                verify: 'Verify phone number',
                step_number_help:
                    'Enter your mobile number, then tap the button below to receive a verification code by SMS.',
                send_cta: 'Send me a code',
                sending: 'Sending…',
                step_code_help: 'We sent an 8-digit code to {phone}.',
                change_number: 'Change number',
                resend_in: 'You can resend the code in {seconds}s',
                resend: 'Resend code',
                verifying: 'Verifying…',
                verify_cta: 'Verify code',
            },
        },
    },
    dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome',
    },
    organizations: {
        organization_saved: 'Organization saved',
        containing_emoji_error:
            "Emoji's are disallowed in the name. Use alphanumerical characters or permitted special characters.",
        invalid_character_error:
            'There is an invalid character in the name. Use alphanumerical characters or permitted special characters.\nInvalid characters: ',
        overview: {
            filters: {
                option_labels: {
                    favorites: {
                        all: 'All',
                    },
                    organization_type: {
                        all: 'All',
                        dance_schools: 'Dance schools',
                        organizers: 'Organizers',
                        artists: 'Teachers en performers',
                        disk_jockeys: "DJ's",
                    },
                },
            },
            no_results: {
                title: 'No organizations found',
                subtitle: 'No organizations found for the selected filters. Please try again with different filters.',
            },
        },
        transfers: {
            overview: {
                title: 'Transfers',
                subtitle: 'Manage the ownership of your organizations',
                incoming_transfers: 'Incoming transfers',
                outgoing_transfers: 'Outgoing transfers',
                request: 'Request',
                offer: 'Offer',
                event: 'Event',
                organization: 'Organization',
            },
            status: {
                approved: 'Transfer Accepted',
                pending: 'Pending',
                rejected: 'Transfer Declined',
            },
            create: {
                title: 'Transfer ownership',
                organization: 'Organization',
                description:
                    'Transfer the ownership of your organization to another user. The new owner will have full control over the organization.',
                reason: 'Reason',
                transfer_to: 'Transfer to (email)',
                transfer: 'Transfer',
                warning:
                    'As soon as the transfer is accepted, the ownership of the organization will be transferred to the new owner. This action is irreversible.',
            },
        },
        remove: {
            label: 'Remove Organization',
            description: 'Permanently delete this organization and all associated data.',
            success: 'Organization successfully deleted',
            error: 'Failed to delete organization',
        },
        transactions: {
            overview: {
                title: 'Transactions',
                subtitle: 'Manage the transactions of your organizations & brands',
            },
        },
        create: 'Create organization',
        is_public_options: {
            private: 'Private',
            public: 'Public',
            private_warning: 'Your organization is not visible online with this status.',
        },
        offline_preview: 'This organization is offline and only visible as a preview.',
    },
    organization: {
        accepts_posts_on_behalf: 'Let LatinMap add our events',
        accepts_posts_on_behalf_help:
            'When our brand is quiet, LatinMap may add publicly announced events under our name. They are clearly marked, and you can edit or remove them at any time.',
        'on-behalf-of': 'On behalf of',
        'on-behalf': {
            none: 'Select your option',
            me: 'Me, I represent this brand',
            other: 'As a representative of the brand',
            noone: 'As ambassador of LatinMap',
        },
        featured: {
            title: 'Featured Dance Schools',
            subtitle:
                'Some of the featured, top-rated dance academies with dance courses now available. Book a class, learn to dance and have fun!',
        },
        my_favorites: {
            title: 'My favorites',
        },
        edit: {
            public_information: 'Information',
            customers: 'Customers',
            events: 'Events',
            tickets: 'Tickets',
            authorized_person: 'Authorized people',
        },
        organization_detail: {
            involved: 'Involved',
            verification_status: {
                verified: 'Verified',
                not_verified: 'Not yet verified',
            },
        },
        account: {
            title: 'Account',
            description: 'Manage your account information for the professional dashboard.',
            description_not_verified: 'Please verify phone number in order to use the professional dashboard.',
        },
        transfer: {
            title: 'Transfer ownership',
            description:
                'Do you want to request the ownership of this organization? Fill in the form and submit your request.',
            what: 'What is offered?',
            submit: 'Submit request',
            name: 'Name',
            location: 'Location',
            email: 'Email',
            reason: 'Reason',
            organization: 'Organization & brand',
            errors: {
                nameRequired: 'Name is required.',
                emailRequired: 'Email is required.',
                reasonRequired: 'Reason is required.',
            },
            succesfully_sent: 'The transfer request has been successfully sent.',
            error_occurred: 'Something went wrong.',
            noOrganisation:
                'You do not have any organizations yet. Please create an organization first before requesting a transfer.',
        },
        title: 'Organization',
        is_public: 'Is public',
        publicly_visible: 'Publicly visible',
        change_organization: 'Change organization',
        create: 'Create organization',
        name: 'Name',
        name_without_dj: "Name (without 'dj')",
        dj_name_tooltip: "Enter only the name. 'DJ' wil be added automatically",
        email: 'Email',
        phone: 'Phone Number',
        phone_verification_token: 'Phone verification token',
        country: 'Country',
        city: 'City',
        street: 'Street',
        street_number: 'Street number',
        postal_code: 'Postal code',
        type: 'Type',
        question: 'Your organization?',
        types: {
            event: 'Event',
            dancer: 'Dancer',
            latin_music: 'Latin music',
            performer: 'Performer',
            teacher: 'Teacher',
            show_performers_and_teams: 'Show Performers & Teams',
            dj: 'DJ',
            mc: 'MC',
            media: 'Media (photo, video, video editing)',
            brand: 'Dance Shoes, Clothing, Store',
            event_organizer: 'Event organizer',
            school: 'Dance school',
            live_music: 'Live band/music',
            venue: 'Venue',
        },
        short_description: 'Short description',
        long_description: 'Long description',
        photos: 'Photos',
        dance_styles: 'Dance styles',
        music_styles: 'Music styles',
        my_brands: 'My brands',
        my_brands_description: 'Manage your brands',
        overview: {
            title: 'Dance schools',
            subtitle:
                'Find the best dance schools in your area. Learn to dance with the best teachers and enjoy the best dance classes.',
            filters: {
                organization_name: 'Organization name',
                dance_styles: 'Dance styles',
                postal_code: 'Postal code',
                nearby_range: 'Nearby postal code suggestions range',
                nearby: 'Nearby suggestions',
                location: 'Where',
                favorites: 'Favorites',
                country_codes: 'Country codes',
                music_styles: 'Music styles',
                ambassadors: 'Ambassadors',
                slidertooltip:
                    '<p><strong>The range slider adjusts the digits of the entered postal code to expand the search area.</strong><p>Each step increases the postal code by 100.</p><p> For example, at step 1, the postal code is increased by 100, and at step 10, it is increased by 1000.',
            },
            organizers: {
                title: 'Organizers',
                subtitle: 'Find the best organizers in your area. Enjoy the best events and festivals.',
            },
            artists: {
                title: 'Teachers en performers',
                subtitle: 'Find the best artists in your area. Enjoy the best events and festivals.',
            },
            dance_schools: {
                title: 'Dance schools',
                subtitle:
                    'Find the best dance schools in your area. Learn to dance with the best teachers and enjoy the best dance classes.',
            },
            disk_jockeys: {
                title: "DJ's",
                subtitle: "Find the best DJ's in your area. Enjoy the best events and festivals.",
            },
            show_performers_and_teams: {
                title: 'Show Performers & Teams',
                subtitle: 'Find the best performers in your area. Enjoy the best events and festivals.',
            },
            teacher: {
                title: 'Teachers',
                subtitle: 'Find the best teachers in your area. Enjoy the best events and festivals.',
            },
            mcs: {
                title: "MC's",
                subtitle: "Find the best MC's in your area. Enjoy the best events and festivals.",
            },
            live_music: {
                title: 'Live bands/music',
                subtitle: 'Find the best live bands and music in your area. Enjoy the best events and festivals.',
            },
            live_performer: {
                title: 'Live performer',
                subtitle: 'Find the best live performers in your area. Enjoy the best events and festivals.',
            },
            performer: {
                title: 'Performer',
                subtitle: 'Find the best live performers in your area. Enjoy the best events and festivals.',
            },
            media: {
                title: 'Media (photo, video, video editing)',
                subtitle: 'Find the best photographers and videographers in your area.',
            },
            brand: {
                title: 'Dance Shoes, Clothing, Store',
                subtitle: 'Find the best dance shoes and clothing in your area.',
            },
        },
        artists: {
            title: 'Teachers en performers',
        },
        teacher: {
            title: 'Teachers',
        },
        performer: {
            title: 'Performers',
        },
        show_performers_and_teams: {
            title: 'Show Performers & Teams',
        },
        dance_schools: {
            title: 'Dance schools',
        },
        disk_jockeys: {
            title: "DJ's",
        },
        organizers: {
            title: 'Organizers',
        },
        manage_authorized_people: {
            title: 'Manage Authorized people',
            description:
                'Manage the authorized people of the current selected organization. Authorized people can manage your organization and events.',
        },
        authorized_people: {
            title: 'Authorized people',
            description:
                "Manage the authorized people of the organizations you're the owner of. Authorized people can manage your organization and events.",
            create: 'Create authorized person',
            edit: 'Edit authorized person',
            overview: 'Authorized people',
            not_found: 'No authorized people found',
        },
        mcs: {
            title: "MC's",
        },
        live_music: {
            title: 'Live bands/music',
        },
        live_performer: {
            title: 'Live performers',
        },
        media: {
            title: 'Media (photo, video, video editing)',
        },
        brand: {
            title: 'Dance Shoes, Clothing, Store',
        },
        email_authorized: 'Enter an existing email address.',
    },
    ambassador: {
        profile: 'Profile',
        my_brands: 'My brands',
        verification: {
            phone: {
                description:
                    "Phone verification is needed to ensure the security and authenticity of users accessing the 'ambassador' dashboard. It works by sending a verification code to your phone, which you must enter to confirm your identity.",
            },
        },
    },
    brands: {
        create: 'Create brand',
        brand_saved: 'Brand saved',
        change_brand: 'Change brand',
        posted_by_ambassador_text:
            'This brand was created by a LatinMap Ambassador, a trusted community member contributing to platform content.',
    },
    contact: {
        title: 'Contact',
        name: 'Name',
        email: 'Email',
        message: 'Message',
        subject: 'Subject',
        send: 'Send',
        success: 'Message sent',
        error: 'Message could not be sent',
    },
    ticketstore: {
        select_amount: 'Select ticket amount',
        your_information: 'Your information',
        total: 'Total',
        next: 'Next',
        back: 'Back',
        checkout: 'Checkout',
        tickets_for: 'Tickets for',
        ticket_types: 'Ticket types',
        ticket: {
            starts_at: 'Starts at',
            location: 'Location',
            name: 'Name',
            description: 'Description',
            tickets_sold: 'Tickets Sold',
            actions: 'Actions',
            available: 'Available',
            total_available: 'Total available',
            price: 'Price',
            create: 'Create ticket type',
            edit: 'Edit ticket type',
            success: 'Your ticket type has been submitted successfully.',
            remove_label: 'Remove ticket type',
            remove_description:
                'This will remove the ticket type and all associated data. This action is irreversible.',
        },
    },
    events: {
        owned_by_organization: 'Owned by organization {organization_name}',
        select_category: 'Select a category',
        select_country: 'Select a country',
        select_timezone: 'Select a timezone',
        select_status: 'Select a status',
        select_currency: 'Select a currency',
        select_brand_error: 'No brand selected',
        title: 'Events',
        bring_online: 'Bring online',
        take_offline: 'Take offline',
        by: 'Events by {organization_name}',
        create_new: 'Create new event',
        event_saved: 'Event saved',
        event_published: 'Your event is now live and visible on the Events page.',
        error_saving: 'Error saving event',
        ticket_sale: {
            button: 'Buy tickets',
            field_label: 'Ticket sale URL',
            field_hint:
                'Direct link where visitors buy tickets or register (e.g. Eventix, Eventbrite, your own ticket shop). Not a social or general promo link.',
            leave_title: 'You are leaving LatinMap',
            leave_message:
                "You're about to leave LatinMap and will be redirected to the organizer's official ticket page.",
            proceed: 'Continue',
            cancel: 'Cancel',
            redirect_failed: 'Could not open the ticket page. Please try again.',
        },
        containing_emoji_error:
            "Emoji's are disallowed in the name. Use alphanumerical characters or permitted special characters.",
        invalid_character_error:
            'There is an invalid character in the name. Use alphanumerical characters or permitted special characters.\nInvalid characters: ',
        duration_too_long:
            'The duration of this event is too long, the maximum amount of days an event is allowed to take is: ',
        error_updating_ticket_type: 'An error occurred while saving the ticket type.',
        ticket_type_updated: 'The ticket type has been updated successfully.',
        posted_by_ambassador: 'Posted by LatinMap Ambassador',
        posted_by_ambassador_text:
            'This event was created by a LatinMap Ambassador, a trusted community member contributing to platform content.',
        posted_on_behalf_text:
            'LatinMap added this event based on publicly available information. The organiser has not yet updated this event information themselves.',
        posted_on_behalf_label: 'added by LatinMap',
        // LA-202: the public pill on the event page. Separate from the
        // line above, which is appended to a title in the organiser's own
        // list and reads as a note rather than a credit.
        posted_by_latinmap: 'Posted by LatinMap',
        not_posted_by_ambassador: 'Verified by organizer',
        participants: {
            create: {
                title: 'Create participant for "{event_name}"',
                description: 'Create a new participant for the event "{event_name}"',
                invite: 'Invite participant',
                role: 'Role',
            },
            role: {
                performer: 'Performer',
                organizer: 'Organizer',
            },
            overview: {
                participants: 'Participants',
            },
        },
        statuses: {
            active: 'Active',
            canceled: 'Canceled',
            changed: 'Changed',
            pending: 'Pending',
        },
        currencies: {
            none: 'No currency',
            euro: 'Euro',
            dollar_american: 'American dollar',
            pound_uk: 'English pound',
            lira_turkish: 'Turkish lira',
            rupee_indian: 'Indian rupee',
        },
        event_detail: {
            cta_block: {
                title: 'Tickets',
                subtitle: 'There are tickets available for this event!',
                button: 'Get your tickets now',
            },
            organizer: {
                plural: 'Organizers',
                singular: 'Organizer',
                invariable: 'Organizer(s)',
            },
        },
        overview: {
            title: 'Events',
            eyebrow: 'Latin Dance Agenda',
            hero_lead: 'Where the floor',
            hero_accent: 'never sleeps.',
            hero_lede:
                'Salsa. Bachata. Kizomba. Zouk. Every party, festival, workshop and social — sorted by the night you want to dance.',
            events_live: 'events live',
            week_short: 'Week',
            view: {
                list: 'List',
                month: 'Month',
            },
            nearby_events: 'Nearby events',
            subtitle:
                "Immerse yourself in the rhythmic beats, exhilarating moves, and cultural fusion of salsa, bachata, and more. Discover a diverse array of events, from energetic dance parties to educational workshops led by renowned instructors. Whether you're a seasoned dancer or just starting your Latin dance journey, our website is your ultimate guide to finding the most sensational festivals and congresses near you. Join us and let the music ignite your passion for Latin dance!",
            festival: {
                title: 'Dance festivals and congresses',
            },
            try_out: {
                title: 'Try-outs, Open day, Start Course(s)',
            },
            party_social: {
                title: 'Parties and socials',
            },
            no_results: {
                title: 'No events found',
                subtitle: 'No events found for the selected filters. Please try again with different filters.',
            },
            filters: {
                search_section: 'Search',
                when_section: 'When',
                personal_section: 'Personal',
                where_section: 'Where',
                styles_section: 'Categories & Styles',
                refine: 'Refine',
                applied: 'Applied',
                clear_all: 'Clear all',
                event_name: 'Event or Organisation name',
                event_id: 'Event ID',
                event_category: 'Event type',
                dance_styles: 'Dance styles',
                dance_style: 'Dance style',
                music_styles: 'Music styles',
                music_style: 'Music style',
                ambassadors: 'Ambassadors',
                status: 'Status',
                user_name: 'User',
                user_type: 'User type',
                organization: 'Organization',
                organization_type: 'Organization type',
                location: 'Where',
                date: 'When',
                date_from: 'From',
                date_to: 'To',
                apply_filters: 'Apply filters',
                clear_filters: 'Clear filters',
                country_codes: 'Country codes',
                city: 'City',
                favorites: 'Favorites',
                postal_code: 'Postal code',
                nearby_range: 'Nearby postal code suggestions range',
                apply: 'Apply',
                sliderinfo: 'Increments of 100',
                slidertooltip:
                    '<p><strong>The range slider adjusts the digits of the entered postal code to expand the search area.</strong><p>Each step increases the postal code by 100.</p><p> For example, at step 1, the postal code is increased by 100, and at step 10, it is increased by 1000.',
                option_labels: {
                    date_filter: {
                        upcoming: 'Upcoming',
                        this_week: 'This week',
                        next_week: 'Next week',
                        in_two_weeks: 'In two weeks +',
                        this_month: 'This month',
                        specific_dates: 'Specific dates',
                        all: 'All',
                    },
                    favorites: {
                        all: 'All',
                        favorites: 'Show only events of my favorited brands',
                        likes: 'Show only my liked events',
                        favorites_likes: 'Show liked events or events of favorited brands',
                    },
                    organization_type: {
                        all: 'All',
                        dance_schools: 'Dance schools',
                        organizers: 'Organizers',
                        music_styles: 'Music styles',
                        music_style: 'Music style',
                        artists: 'Teachers en performers',
                        disk_jockeys: "DJ's",
                    },
                    user_type: {
                        professional: 'Professional',
                        ambassador: 'Ambassador',
                        admin: 'Admin',
                        superadmin: 'Superadmin',
                    },
                },
            },
            workshop_bootcamp: {
                title: 'Workshops & Bootcamps',
            },
        },
        type: {
            festivals_and_congresses: 'Festivals & Congresses',
            try_out: 'Try-outs, Open day, Start Course(s)',
            live_music: 'Live bands/music',
            workshop_bootcamp: 'Workshops & Bootcamps',
        },
        categories: {
            festival: 'Festival/Congress',
            workshop_bootcamp: 'Workshop/Bootcamp',
            workshop_bootcamp_party: 'Party with workshop/bootcamp',
            party_social: 'Party/Social',
            competition: 'Competition',
            performance_concert: 'Performance/Concert',
            outdoor_event: 'Outdoor event',
            dance_trip: 'Dance trip',
            party_live_music: 'Party/Live music',
            online_event: 'Online event',
            try_out: 'Try-outs, Open day, Start Course(s)',
            show: 'Show',
            marathon: 'Marathon',
            weekender: 'Weekender',
            retreat: 'Retreat',
            other: 'Other',
        },
        create: 'Create event',
        edit: 'Edit event',
        remove: {
            label: 'Remove event',
            description: 'This will remove the event and all associated data. This action is irreversible.',
        },
        brand: 'Organization/Brand',
        name: 'Name',
        country: 'Country',
        city: 'City',
        street: 'Street',
        street_number: 'Street number',
        short_description: 'Short description',
        long_description: 'Long description',
        social_preview_description: 'Social preview description',
        social_preview_description_hint:
            'Optional. A short teaser (max 280 characters) used for the Share Link preview when the event is shared on WhatsApp, Facebook or LinkedIn. Left empty, the short description is used.',
        share: {
            title: 'Share Link',
            copy_share_link: 'Copy Share Link',
            share_link_help:
                'Use this link when sharing on WhatsApp, Facebook or LinkedIn. It shows the correct preview and opens the complete event page with all the info.',
            edit_hint:
                'Share this link (not the browser address) on social media so your event shows the correct preview.',
        },
        dance_styles: 'Dance styles',
        music_styles: 'Music styles',
        photos: 'Photos',
        photos_preview_mode: 'Photos are visible after saving in preview mode',
        preview: 'Show',
        category: 'Event type',
        language: 'Language',
        location_type: {
            singular: 'Location type',
            online: 'Virtual',
            offline: 'In person',
        },
        timezone: 'Timezone',
        date: 'Date',
        week: 'Week',
        start: 'Start',
        end: 'End',
        location: 'Location',
        information: 'Information',
        start_date: 'Start date',
        start_time: 'Start time',
        end_date: 'End date',
        end_time: 'End time',
        ends_at: 'Ends at',
        registration_opens_at: 'Registration opens at',
        registration_closes_at: 'Registration closes at',
        is_public: 'Ready to go live?',
        location_name: 'Venue name',
        photos_required: 'At least one photo is required.',
        tickets_sold: 'Tickets sold',
        actions: 'Actions',
        participate: 'Participate',
        verified: 'Brand official verified',
        undercondition: 'Not confirmed yet',
        warning: 'Important change - Last update:',
        ticket: 'Tickets available',
        cancelled: 'Cancelled - Last update:',
        upcoming: 'Upcoming events',
        past: 'Past events',
        status: 'Status',
        status_reason: 'Status reason',
        entrance: 'Ticket Prices',
        entrance_tooltip:
            "Here you can specify the ticket price. Enter a number if the price does not change, for example: '5'. for a price range you can specify a range, for example '5-10'. Use 'free' if there is no entry price.",
        currency: 'Currency',
        currency_tooltip:
            "It is also possible to specify the currency. Frequently used currencies are: euro (€), American dollar ($), English pound (£), Turkish lira (₺), and Indian rupee (₹). If no currency is selected, you can add any text, like '5-12 euro'.",
        repeat: 'Repeats',
        djs: "DJ's",
        dj: 'DJ',
        artists: 'Artists',
        artist: 'Artist',
        question: 'Your event?',
        status_options: {
            active: 'Active',
            canceled: 'Canceled',
            pending: 'Not confirmed yet',
            changed: 'Important change',
        },
        is_public_options: {
            draft: 'Draft',
            private: 'Private',
            public: 'Public',
            draft_message: 'Save your event as a draft. It will not be visible to the public.',
            private_message: 'Your event will be visible only to users with access.',
            public_message: 'Publish your event to make it visible on the Events page.',
            private_warning: 'Your event is not visible online with this status.',
        },
        legend: {
            title: 'Legend',
            verified: 'Brand official verified',
            not_verified: 'Not verified by organization: the organizer has not confirmed the activity yet.',
            cancelled: 'Cancelled',
            warning: 'Important change',
            posted_by: 'Posted by LatinMap Ambassador',
            condition: 'Not confirmed yet',
            ticket: 'Tickets available',
            duplicate_event_error: 'An event with this name, start date, and end date already exists',
            ticket_order: 'Tickets available, order them here!',
        },
        ticket_types: 'Ticket types',
        potential_duplicates: 'Possible duplicates',
        proceed_anyway: 'Save despite duplicate',
        duplicate_event_error: 'An event with this name, start date, and end date already exists',
        event_deleted: 'Event deleted',
        error_deleting_event: 'Error deleting event',
        featured: {
            title: 'Featured events in the community',
            subtitle: 'Discover and get your tickets to dance festivals, concerts and trips at the best price!',
            badge: 'Featured',
            view: 'View event',
            title_page: 'Change global price',
            featured_event: 'Buy Featured Event',
            duration_type: 'Duration Type',
            day: 'Day',
            month: 'Month',
            year: 'Year',
            duration: 'Duration',
            price: 'Price',
            calculate_price: 'Calculate Price',
            proceed_to_payment: 'Proceed to Payment',
            current_price: 'Current Price',
            no_price_found: 'No price found for the selected duration',
            update_price: 'Update Price',
            setFeaturedEvents: 'Set Featured Event',
            fetch_current_price: 'Fetch Current Price',
            update_global_event_price: 'Update Global Event Price',
            price_per_day: 'Price per day',
            buy: 'Buy featured event',
            duration_label: 'For how long would you like to feature the event?',
            enter_duration: 'Select Duration',
            total_price: 'Total',
            yes: 'Yes',
            no: 'No',
            invalid_invoice: 'Something went wrong, try again',
            download_failed: 'Something went wrong, try again',
            setFeaturedEvent: 'Set Featured Event for:',
            setFeatured: 'Set featured event',
            featured_success: 'The event has been successfully featured.',
            already_featured: 'This event is already featured',
            featured_error: 'Something went wrong',
            durationError: 'Value cannot be less than 1',
        },
    },
    blocked_user: {
        create: 'Create blocked user',
        edit: 'Update blocked user',
        objection: 'Objection',

        blocked_email: 'Blocked email',
        blocked_phone_number: 'Blocked phone number',
        reason: 'Reason',

        delete: {
            warning: {
                title: 'Delete user blocking',
                body: 'Are you sure you want to delete this block. When this block is deleted users that were previously blocked by this blocking will have all their rights back.',
            },
        },
    },
    objection: {
        plural: 'Objections',

        remove_blocking: 'Remove blocking',
        decline: 'Decline objection',

        response: {
            unauthorized: 'Invalid objection, not authorized.',
            exists: 'An objection already exists.',
            success: 'Objection successfully created.',
        },
    },
    carousel: {
        title: 'Discover Latin Dance - Events, Schools, Try-Outs & More Near You!',
        subtitle: 'Find where to dance, learn, and connect with the Latin community.',
        search: {
            placeholder: 'Enter a keyword or city to explore Latin dance options...',
            button: 'DISCOVER',
        },
    },
    home: {
        // LA-179: the official Instagram strip that closes the homepage.
        instagram: {
            title: 'Live from the dance floor',
            subtitle: 'The latest from our Instagram — the scene as it actually looks, week by week.',
            follow: 'Follow {handle}',
            post_alt: 'A post from {handle} on Instagram',
        },
        cta_block_1: {
            title: 'Do you organize dance events?',
            subtitle:
                'If you organize parties, festivals, trips, congresses, weekends, workshops, intensive courses and dance concerts we have something for you, promote your event, manage promoters and sell tickets!',
            button: 'Sign up now',
        },
        cta_block_2: {
            title: 'Explore Events Near You',
            subtitle:
                'Discover nearby events and activities happening in your area. Have a great time exploring and enjoying the experiences!',
            placeholder: 'Your location',
            input_button: 'Near me',
            button: 'Find events',
        },
        cta_block_3: {
            button: 'Create an event',
        },
        experiences_eyebrow: 'Explore',
        choose_and_live_experiences: 'Choose and live experiences',
        experiences_subtitle: 'Pick a vibe and dive straight into the Latin dance scene.',
        experiences: {
            festivals_congresses: 'Festivals & Congresses',
            parties_socials: 'Parties, Socials',
            try_outs_start_courses: 'Try-outs, Start Courses',
            dance_schools: 'Dance Schools',
            workshops_bootcamps: 'Workshops, Bootcamps',
            dancewear_shoes: 'Dancewear & Shoes',
            teachers: 'Teachers',
            show_performers_teams: 'Show Performers & Teams',
            djs: "DJ's",
            live_music: 'Live Bands',
            media_photo_video: 'Media (Photo/Video)',
            mcs: "MC's",
        },
        cta_journey: {
            title: 'Start your LatinMap journey',
            subtitle: 'Choose how you want to experience LatinMap',
            agenda: {
                title: 'Worldwide Agenda',
                sub_header: 'Free to browse',
                subtitle: 'Explore the global Latin dance scene',
                description: 'Browse parties, workshops, festivals, try-outs & start courses - all in one agenda',
                button: 'View Latin Agenda',
                footer: 'No account required',
            },
            member: {
                title: 'Become a Member',
                sub_header: 'Free account',
                subtitle: 'Build your own LatinMap experience',
                description:
                    'Create your personal LatinMap and stay connected to the Latin dance scene — locally and internationally.',
                features: {
                    agenda: 'Complete & up-to-date Latin dance agenda',
                    follow: 'Follow your favorite events, artists & brands',
                    save: 'Save your favorites with one click',
                    store: 'Store your searches & discoveries',
                    custom_agenda: 'Create your own custom agenda',
                    explore: 'Explore new cities, styles & scenes',
                },
                button: 'Create free account',
                footer: 'Free to join – never miss an event again',
            },
            professional: {
                title: 'Become a Professional',
                sub_header: 'Free professional profile',
                subtitle: 'For festivals, dance schools, teachers, DJs, musicians & organizers',
                description: 'Grow your visibility, manage your events and reach the international Latin community.',
                features: {
                    brand_page: 'Your own professional brand page',
                    publish: 'Publish & manage events easily',
                    invite: 'Invite team members to collaborate',
                    insights: 'Insights, statistics & growth tools',
                    exposure: 'International exposure on LatinMap',
                },
                button: 'Activate Pro account',
                footer: 'Requires a Member account first',
            },
        },
    },
    dropzone: {
        drop_here: 'Drop files here to upload',
        files_required: '{count} required',
        max_filesize: 'Maximum file size: {max} MB',
    },
    general: {
        select_organization_please: 'Please select an organization',
        no_organization_selected: 'No organization selected',
        all: 'All',
        styles: 'Styles',
        important_information: 'Important information',
        starts_at: 'From',
        ends_at: 'To',
        socialmedia_links: 'Links',
        share_with_your_friends: 'Share with your friends',
        link_copied: 'Link copied to clipboard!',
        link_copy_failed: 'Failed to copy link.',
        hosted_by: 'Hosted by',
        created_by: 'Created by',
        posted_by: 'Posted by',
        created: 'Created',
        archive: 'Archive',
        modified: 'Modified',
        score: 'Score',
        reset: 'Reset',
        unknown: 'Unknown',
        submit: 'Submit',
        suggest: 'Suggest',
        other: 'Other',
        delete: 'Delete',
        copy_link: 'Copy link',
        back: 'Back',
        show: 'Show',
        duplicate: 'Duplicate',
        proceed: 'Proceed',
        cancel: 'Cancel',
        accept: 'Accept',
        reject: 'Reject',
        show_in_new_window: 'Show in new window',
        checkout: 'Checkout',
        external_link_warning: {
            title: 'External link',
            content: "You're about to leave this site.",
            question: 'Do you want to continue to the following URL?',
        },
        add: 'Add',
        date: 'Date',
        search: {
            save_new: 'Save new search',
            instructions: 'Save your search to use it again later.',
            name: 'Name',
            none_stored: 'You have no saved searches yet.',
            stored: 'Saved searches',
            events: 'Events',
            organizations: 'Organizations',
            paginate: 'Per page',
            page: 'Page',
            view: 'View',
            filters: 'Filters',
            delete: 'Delete',
            views: {
                grid: 'Grid',
                list: 'List',
                agenda: 'Agenda',
                monthlist: 'Month list',
            },
            filter_labels: {
                name: 'Name',
                location: 'Location',
                dance_styles: 'Dance styles',
                music_styles: 'Music styles',
                event_categories: 'Event categories',
                date_filter: 'Date',
                favorites: 'Favorites',
                country_codes: 'Country codes',
                organization_type: 'Organization types',
            },
        },
        socials: {
            facebook: 'Facebook',
            instagram: 'Instagram',
            site: 'Website',
            linkedin: 'Linkedin',
            youtube: 'Youtube',
            tiktok: 'Tiktok',
            reddit: 'Reddit',
            pinterest: 'Pinterest',
            twitter: 'X',
            spotify: 'Spotify',
            soundcloud: 'SoundCloud',
            youtube_video: 'Video (YouTube or Vimeo)',
            facebook_event: 'Facebook Event',
            whatsapp: 'Whatsapp',
        },
        description: {
            title: 'Description',
            show_more: 'Show more',
            show_less: 'Show less',
        },
        map: 'Map',
        gmap_block: {
            title: 'Map',
            location_name: 'Location name',
        },
        spotify_block: {
            title: 'Spotify',
        },
        soundcloud_block: {
            title: 'SoundCloud',
        },
        paginate: {
            results: 'Results',
            filter: 'Filter',
            open_filter: 'Open filter',
            page: 'page',
        },
        edit: 'Edit',
        create: 'Create',
        expand: 'More',
        more_info: 'More info',
        collapse: 'Less',
        unique_impressions: 'Unique impressions',
        close: 'Close',
        save_changes: 'Save changes',
        update_changes: 'Save changes',
        scrollright: 'Scroll right for more details.',
    },
    header: {
        title: 'LatinMap',
        quote: 'The heartbeat of the Latin dance scene',
        navigation: 'Navigation',
        cta: 'Create event',
    },
    date: {
        from: 'From',
        to: 'To',
    },
    report: {
        event: 'Report Event',
        organization: 'Report Organization',
        name: 'Name',
        location: 'Location',
        date: 'Date',
        success: 'Your report has been submitted successfully.',
        not_logged_in: 'Account required',
        tooltip: 'Report',
        status: {
            warning: {
                title: 'Update report status',
                body: 'Are you sure you want to change the report status of this report?',
            },
        },
        archive: {
            warning: {
                title: 'Archive report',
                body: 'Are you sure you want to archive this report? This cannot be undone.',
            },
        },
    },
    form: {
        save: 'Save',
        save_and_verify: 'Save and verify',
        too_many_requests:
            'You have reached the message limit. ' + 'Please wait a few minutes before sending another request.',
    },
    form_subject: {
        general_inquiry: 'General Inquiry',
        technical_support: 'Technical Support',
        issue_event_submission: 'Event Submission Issue',
        issue_account_login: 'Account or Login Issue',
        question_billing_payment: 'Billing or Payment Question',
        report_issue: 'Report an Issue',
        suggest_feature: 'Feature Suggestion',
        other: 'Other',
    },
    api: {
        general: {
            validation_error: 'Validation error',
            permission_denied: 'Permission denied',
            success: 'Success',
        },
        auth: {
            unauthorized: 'You are not authorized to access this resource.',
            blocked: 'Your account has been blocked, you will not be able to access any features.',
            user_not_found: 'An account is required to use this functionality, please make an account first.',
            email_password_unknown: 'The provided email address or password is incorrect.',
            email_unverified: 'Your email address is not verified.',
            email_already_verified: 'Your email address is already verified.',
            phone_already_verified: 'Your phonenumber is already verified.',
            phone_already_used: 'This telephone number is already in use',
            invalid_verification_link: 'The provided verification link is invalid.',
            email_verified_success: "Your email address has been verified. You'll be redirected automatically.",
            verification_email_sent: 'A verification link has been sent to your email address.',
            login_success: 'You have successfully logged in.',
            logout_success: 'You have successfully logged out.',
            registration_success:
                'You have successfully registered. A verification link has been sent to your email address.',
            failed: 'These credentials do not match our records.',
            password: 'The provided password is incorrect.',
            throttle: 'Too many login attempts. Please try again in :seconds seconds.',
            user_updated_success: 'Your account has been updated successfully.',
            old_password_incorrect: 'The provided old password is incorrect.',
            password_changed_success: 'Your password has been changed successfully.',
            user_deleted_success: 'Your account has been deleted successfully.',
            verification_text_sent: 'A verification text has been sent to your phone number.',
            invite_invalid: 'The used invite url is invalid.',
            too_many_requests: 'Too many requests. Please try again later.',
            day_limit_exceeded:
                'You exceeded the maximum amount of requests for this functionality today. Try again tomorrow.',
        },
        organization: {
            created_success: 'The organization has been created successfully.',
            authorized_person: {
                created: 'The authorized person has been created successfully.',
                updated: 'The authorized person has been updated successfully.',
                removed: 'The authorized person has been removed successfully.',
                not_found: 'The authorized person could not be found.',
                already_exists: 'The authorized person already exists.',
                role: {
                    singular: 'Role',
                    invalid: 'The provided role is invalid.',
                    eventPublisher: 'Event Publisher',
                    eventCreator: 'Event Creator',
                    authorizedPerson: 'Authorized Person',
                },
            },
        },
        event: {
            created_success: 'The event has been created successfully.',
            deleted_success: 'The event has been deleted successfully.',
        },
        report: {
            created_success: 'The report has been created successfully.',
            invalid_type: 'The provided report type is invalid.',
        },
        user: {
            updated_success: 'The user has been updated successfully.',
            search_filter: {
                created: 'Created',
            },
        },
        upload: {
            created_success: 'The upload has been created successfully.',
            deleted_success: 'The upload has been deleted successfully.',
        },
        forms: {
            submitted_success: 'The form has been submitted successfully.',
        },
        passwords: {
            reset: 'Your password has been reset.',
            sent: 'We have emailed your password reset link!',
            throttled: 'Please wait before retrying.',
            token: 'This password reset token is invalid.',
            user: "We can't find a user with that email address.",
        },
        pagination: {
            previous: '&laquo; Previous',
            next: 'Next &raquo;',
        },
        dance_style: {
            saved_success: 'The dance style has been saved.',
            deleted_success: 'The dance style has been deleted successfully.',
            deleted_error: 'Error deleting dance style',
            not_found: 'The dance style could not be found.',
            parent_not_found: 'The parent dance style could not be found.',
            suggest_success: 'Dance style suggestion sent.',
        },
        music_style: {
            saved_success: 'The music style has been saved.',
            deleted_success: 'The music style has been deleted successfully.',
            not_found: 'The music style could not be found.',
            parent_not_found: 'The parent music style could not be found.',
            suggest_success: 'Music style suggestion sent.',
        },
        invite: {
            sent: 'Invitation sent',
            removed: 'Invitation removed',
            resend_success: 'Invitation resent',
        },
        setting: {
            update_success: 'Setting was updated successfully.',
        },
        transfer_request: {
            already_owner: 'You are already the owner of this organization.',
            organization_already_owns_event: 'The organization already owns the event.',
        },
        transferlink: {
            removed: 'The transferlink has been removed.',
        },
        user_block: {
            create_success: 'User block was successfully created.',
            update_success: 'User block was successfully updated.',
            delete_success: 'User block was successfully deleted.',
        },
    },
    favorites: {
        favorite: 'Favorite',
        like: 'Like',
        login_required: 'You must be logged in to add this to your favorites.',
    },
    likes: {
        login_required: 'You must be logged in to add this to your likes.',
    },
    ticketing: {
        stats: {
            title: 'Ticket clicks',
            loading: 'Loading statistics',
            error: 'Could not load statistics.',
            empty: 'No ticket clicks recorded yet.',
            total: 'Total',
            this_month: 'This month',
            last_12_months: 'Last 12 months',
            per_month: 'Per month',
            top_events: 'Top events',
            by_device: 'By device',
            by_destination: 'By destination',
            device: {
                mobile: 'Mobile',
                desktop: 'Desktop',
                tablet: 'Tablet',
                unknown: 'Unknown',
            },
            destination: {
                external: 'External',
                internal: 'LatinMap',
            },
        },
        internal: {
            toggle_label: 'Internal ticketing (LatinMap ticket store)',
            toggle_hint:
                "When on, this brand's \"Buy tickets\" buttons open LatinMap's own ticket store instead of the organizer's external URL (only for events that have ticket types).",
            updated: 'Internal ticketing setting saved.',
            update_failed: 'Could not save the internal ticketing setting.',
        },
    },
    validations: {
        validations: {
            doesnt_exist: {
                'owner id': 'Could not find the user to create this organisation on behalf of.',
            },
        },
        on_behalf_of: {
            required: 'The on behalf of field is required.',
        },
        required: 'This field is required.',
        phone: {
            required: 'The phone number is required.',
            regex: 'The phone number is invalid.',
            length: 'The phone number must be at least 10 digits long.',
        },
        'phone number': {
            required: 'The phone number is required.',
            regex: 'The phone number is invalid.',
            'not-unique': 'This phone number is already in use.',
        },
        recaptcha: {
            failed: 'The recaptcha failed.',
        },
        number: {
            required: 'The number is required.',
            invalid: 'The number is invalid.',
        },
        email: {
            required: 'The email address is required.',
            invalid: 'The email address is invalid.',
            'not-unique': 'The email addres is not unique.',
        },
        name: {
            required: 'The name is required.',
            max_length: 'The name is too long. Maximum 60 characters allowed.',
        },
        username: {
            required: 'The username is required.',
            invalid: 'The username is invalid.',
        },
        first_name: {
            required: 'The first name is required.',
        },
        last_name: {
            required: 'The last name is required.',
        },
        gender: {
            required: 'The gender is required',
        },
        city: {
            required: 'The city is required.',
        },
        street: {
            required: 'The street is required.',
        },
        street_number: {
            required: 'The street number is required.',
        },
        country: {
            required: 'The country is required.',
        },
        brand: {
            required: 'The brand is required.',
        },
        short_description: {
            required: 'The short description is required.',
        },
        long_description: {
            required: 'The long description is required.',
        },
        description: {
            required: 'The description is required.',
        },
        type: {
            required: 'The type is required.',
        },
        message: {
            required: 'The message is required.',
        },
        date_of_birth: {
            required: 'The date of birth is required.',
            regex: 'The date of birth is invalid.',
            age: 'You must be at least 18 years old.',
            old: 'You must be at most 120 years old.',
        },
        date: {
            required: 'The date is required.',
            regex: 'The date is invalid.',
        },
        postal_code: {
            required: 'The postal code is required.',
            length: 'The postal code must be at least 4 digits long.',
        },
        entrance: {
            required: 'The Ticket Prices are required.',
        },
        repeat: {
            required: 'The repeat is required.',
        },
        password: {
            required: 'The password is required.',
            regex: 'Valid password required. Minimum 8 characters, at least one uppercase letter, one lowercase letter and one number.',
            confirmed: 'The password confirmation does not match.',
            mismatch: 'The password does not match.',
        },
        conditions: {
            required: 'You must accept the terms and conditions.',
        },
        url: {
            required: 'The url is required.',
            regex: 'The url is invalid.',
        },
        event_category: {
            required: 'The event type is required.',
        },
        language_code: {
            required: 'The language is required.',
        },
        timezone: {
            required: 'The timezone is required.',
        },
        is_public: {
            required: 'The visibility is required.',
        },
        form_subject: {
            required: 'The subject is required.',
        },
        starts_at: {
            required: 'The start date is required.',
            regex: 'The start date is invalid.',
        },
        start_date: {
            required: 'The start date is required.',
            regex: 'The start date is invalid.',
            after_end_date: 'The start date must be before the end date.',
        },
        start_time: {
            required: 'The start time is required.',
            regex: 'The start time is invalid.',
        },
        end_date: {
            required: 'The end date is required.',
            regex: 'The end date is invalid.',
            before_start_date: 'The end date must be after the start date.',
        },
        end_time: {
            required: 'The end time is required.',
            regex: 'The end time is invalid.',
        },
        ends_at: {
            required: 'The end date is required.',
            regex: 'The end date is invalid.',
            before_start_date: 'The end date must be after the start date.',
        },
        registration_opens_at: {
            required: 'The registration start date is required.',
            regex: 'The registration start date is invalid.',
        },
        registration_closes_at: {
            required: 'The registration end date is required.',
            regex: 'The registration end date is invalid.',
        },
        location_type: {
            required: 'The location type is required.',
        },
        location_name: {
            required: 'The location name is required.',
        },
        dance_styles: {
            required: 'The dance styles are required.',
        },
        music_styles: {
            required: 'The music styles are required.',
        },
        photos: {
            required: 'At least 1 photo is required.',
        },
        status: {
            required: 'The status is required.',
        },
        string: {
            required: '{fieldname} is required.',
            mismatch: '"{input}" does not match "{safeword}".',
        },
        role: {
            required: 'The role is required.',
        },
        favorite: {
            required: 'Favorite is required.',
        },
        dance_style: {
            required: 'Dance style is required.',
        },
        facebook: {
            required: 'The Facebook link is required.',
            regex: 'Please enter a valid Facebook link.',
        },
        facebook_event: {
            required: 'The Facebook Event link is required.',
            regex: 'Enter a Facebook Event URL — must point to facebook.com/events/{id}.',
        },
        instagram: {
            required: 'The Instagram link is required.',
            regex: 'Please enter a valid Instagram link.',
        },
        tiktok: {
            required: 'The TikTok link is required.',
            regex: 'Please enter a valid TikTok link.',
        },
        website: {
            required: 'The website link is required.',
            regex: 'Please enter a valid website link.',
        },
        twitter: {
            required: 'The X link is required.',
            regex: 'Please enter a valid X link.',
        },
        linkedin: {
            required: 'The Linkedin link is required.',
            regex: 'Please enter a valid Linkedin link.',
        },
        youtube: {
            required: 'The video link is required.',
            regex: 'Please enter a valid YouTube or Vimeo link.',
        },
        ticket_url: {
            required: 'The ticket sale URL is required.',
            regex: 'Enter a valid ticket URL starting with http:// or https://.',
            social_not_allowed: 'This must be a ticket or registration link, not a social media or general promo URL.',
        },
        spotify: {
            required: 'The Spotify link is required.',
            invalid: 'Please enter a valid Spotify link in the correct format.',
            regex: 'Please enter a valid Spotify link in the correct format.',
        },
        soundcloud: {
            required: 'The SoundCloud link is required.',
            invalid: 'Please enter a valid SoundCloud link in the correct format.',
            regex: 'Please enter a valid SoundCloud link in the correct format.',
        },
        doesnt_exist: {
            email: 'The provided email address does not exist.',
            user: 'The provided user does not exist.',
        },
        djs: {
            required: "The DJ's are required.",
        },
        artists: {
            required: 'The artists are required.',
        },
        reason: {
            required: 'The reason is required.',
        },
        default: {
            doesnt_exist: {
                email: 'The provided email address does not exist.',
                user: 'The provided user does not exist.',
            },
        },
    },
    brand_activity: {
        by_brand: 'brand user',
        by_admin: 'admin/scraping',
        export: 'Export CSV',
    },
    paginatable: {
        headers: {
            is_dancer: 'Dancer',
            is_professional: 'Professional',
            newsletter_optin: 'Newsletter',
            last_event_start_date: 'Last event date',
            last_event_at: 'Last event activity',
            last_event_by: 'Last event by',
            has_upcoming: 'Upcoming published',
            id: 'ID',
            brand_id: 'Brand ID',
            title: 'Title',
            slug: 'Slug',
            group: 'Group',
            email: 'E-mail',
            phone: 'Phone number',
            name: 'Name',
            first_name: 'First name',
            last_name: 'Last name',
            role_name: 'Role',
            role: 'Role',
            report: 'Report',
            created_at_formatted: 'Created at',
            status: 'Status',
            actions: 'Actions',
            type: 'Type',
            to_user_email: 'To e-mail',
            from_user_email: 'From e-mail',
            reason: 'Reason',
            organization_name: 'Organization name',
            organization_created: 'Organization created',
            organization_updated: 'Organization last updated',
            confirmed_at: 'Confirmed at',
            organization_role: 'Role',
            sub_dance_styles: 'Sub dance styles',
            sub_music_styles: 'Sub music styles',
            city: 'City',
            created_at: 'Created',
            updated_at: 'Modified',
            is_favorite: 'Favorite',
            starts_at: 'Starts at',
            tickets_sold: 'Tickets sold',
            price: 'Price',
            start_date: 'Start date',
            is_public: 'Public',
            available_tickets: 'Available tickets',
            user_id: 'User-ID',
            transferable_type: 'Type',
            content_name: 'Title',
            paynl_transaction_id: 'PayNL transaction',
            date: 'Purchase date',
            amount: 'Total amount',
            tax_amount: 'Tax',
            currency: 'Currency',
            description: 'Description',
            valid_from: 'Start',
            valid_until: 'Expiry',
            items: 'items',
            total: 'Total',
            featured: 'Featured',
            created_by: 'User',
            user_type: 'User Type',
            user_email: 'User Email',
            location_type: 'Location type',
            category_id: 'Category',
            organization_category_id: 'Category',
            country_code: 'Country code',
            location: 'Location',
            info: 'User information',
            owner: 'Representing',
            adres: 'Address',
            export_created: 'Export created',
            image_path: 'Image path',
            order_by: 'Order by',
            visibility: 'Visibility',
            image_alt: 'Image alt',
            image_title: 'Image title',
            image: 'Image',
            public_status: 'Public status',
            country: 'Country',
            activity: 'Activity',
        },
        search: 'Search',
        no_records: 'No results found',
    },
    pages: {
        related: 'Related pages',
        contact: {
            title: 'Contact Latinmap',
            body: 'For questions related to events, please contact the organization that posted the event. Do you have questions or comments about the platform? Please contact us via the form below.',
        },
        'how-it-works': {
            title: 'How It Works',
            body: '<p>Online ticket sales via LatinMap.com</p><p>We offer organizations the possibility of online ticket sales. There are basically no costs for organizations. However, we do charge fees for two or more interim payments.</p><p><br/>&nbsp;</p><p>Good telephone support for ticket buyers and organizations</p><p>We are available by phone for questions from ticket buyers and organizations, 7 days a week, both during and outside office hours.</p><p>How it works for the organization:</p><p>-we create a ticket link for the event. Organizations using our <u>Dashboard</u> can create new tickets themselves via the <u>Dashboard</u>.<br/>-a fee of 1.49 euros per purchase (max 10 tickets) is charged to the ticket buyer when paying with iDeal.<br/>-organizations can choose to take the purchase fee upon themselves<br/>-group tickets can be created<br/>-an end date and end time can be set for ticket sales<br/>-ticket prices can be set per period<br/>-organizations can view the current sales status via a management link<br/>-for each ticket sale, the organization receives a copy email. This ticket can be forwarded to a buyer who reports not receiving the ticket email<br/>-free guest list tickets can be issued via the Dashboard<br/>-free box office tickets can be issued via the Dashboard. These can be sold manually<br/>-a maximum number of tickets per ticket or event that can be sold can be set<br/>-discount codes can be used for ticket sales<br/>-the organization can opt for payout after the event, in this case, we offer a cancellation guarantee: if the event is canceled, LatinMap.com will refund the ticket buyers<br/>-no charges are made for cancellation and refund by LatinMap.com (at 1.49 per purchase or 1 euro per ticket)<br/>-the organization can also receive interim payments. In that case, we do not offer a cancellation guarantee<br/>-for each second or subsequent interim payment, a fee of 10 euros per payment is charged.<br/>-the ticket sales are promoted via our newsletter and the Latin-Magazine to about 10,000 people monthly.</p><p>Ticket handling</p><p>Tickets can be scanned with a mobile phone using the <u>TicketScan App for Android</u> in the Google Play Store. The <u>TicketScan App for iOS</u> is available in the Apple App Store.<br/>Tickets can be processed online. The URL for this can be found in the Dashboard.<br/>A csv export file of the sold tickets can be downloaded, which can be printed or imported into Excel.</p><p>How it works for the ticket buyers:</p><p>-ticket buyers can pay online with iDeal, Bancontact, PayPal, CreditCard, Sofortbanking<br/>-ticket buyers pay a fixed fee per purchase via iDeal/Bancontact, regardless of the number of tickets. For other payment methods, the fee depends on the purchase amount.<br/>-ticket buyers pay a fixed fee per ticket via iDeal/Bancontact. For other payment methods, the fee depends on the purchase amount.<br/>-after payment, the ticket(s) are sent by mail<br/>-ticket buyers can choose from Dutch and English when ordering tickets<br/>-via the payment page, ticket buyers can have their ticket(s) sent again by mail</p>',
        },
        'goods-and-services': {
            title: 'Goods and Services',
            body: '<p>LatinMap.com: Discover and Experience the Latin Dance World</p><p>Welcome to LatinMap.com, the destination where you effortlessly gain access to the most exciting events and experiences in the world of Latin dance. Whether you are a passionate dance enthusiast, a beginner dancer looking to take the first steps, or simply want to enjoy the enchanting rhythms of Salsa, Bachata, Kizomba, Zouk, and more, our extensive dance agenda has something for everyone.</p><p>With LatinMap.com, we offer a wide range of activities, ranging from parties, festivals, and workshops to bootcamps, classes, and dance schools. Whether you want to shine on the dance floor during a lively Salsa party, discover the sensual movements of Bachata, experience the romance of Kizomba, or embrace the seductive sounds of Zouk, our dance agenda has it all.</p><p>In addition, we give you the opportunity to follow your favorite organizers, DJs, dance schools, and artists. Stay informed about their latest events and performances, and never miss a chance to enjoy their unique styles and talents. Discover new artists and enrich your Latin dance experience with the diversity and creativity that the Latin dance community has to offer.</p><p>With our intuitive and user-friendly interface, you can effortlessly navigate through the dance agenda and select your favorite events. Whether you are an experienced dancer looking to refine your technique, or someone who simply wants to enjoy the exciting atmosphere of Latin dance, LatinMap.com is here for you.</p><p>We strive for a seamless user experience and offer excellent support for any questions or potential issues you may encounter. Our dedicated team of professionals is ready to assist you and ensure that your dance experience goes without any obstacles.</p><p>Discover, experience, and create memories with LatinMap.com. We believe that access to the most exciting Latin dance events enriches your passion for dance and offers you the opportunity to grow and enjoy the rich Latin dance culture. Let us guide you on your journey to unforgettable dance experiences.</p>',
        },
        'terms-and-conditions': {
            title: 'Terms and Conditions',
            body: "<p>20230529 Terms and Conditions of Ticket Sales</p><p>1. Definitions</p><p>The terms defined here with an initial capital letter have the following meaning in these Terms and Conditions:<br/>a. Terms and Conditions: these general ticket sales conditions of the LatinMap.com Foundation<br/>b. Event: the organized event, such as an event of musical, cultural, sports, and/or recreational nature, for which the LatinMap.com Foundation sells Tickets<br/>c. Event Organizer: the natural or legal person who produces or organizes an Event and is responsible for it<br/>d. Customer: the natural or legal person who enters into an agreement with the LatinMap.com Foundation relating to an Event, or to whom the LatinMap.com Foundation makes an offer to do so<br/>e. Agreement: the agreement between the LatinMap.com Foundation and Customer for the acquisition of Tickets for Events<br/>f. Ticket: the document that serves as an admission ticket for an Event, including electronic Tickets</p><p>2. Applicability</p><p>a. These Terms and Conditions apply to all offers from, orders with, legal relations, and agreements in which the LatinMap.com Foundation delivers products or services of any kind to Customer, even if these products or services are not (further) described in these Terms and Conditions.<br/>b. The LatinMap.com Foundation expressly rejects the applicability of any other conditions of the Customer.<br/>c. Parties can only agree on deviations from the Terms and Conditions in writing. The LatinMap.com Foundation is entitled to change the Terms and Conditions and to declare changed general terms and conditions applicable to Agreements. The amended general terms and conditions can be consulted on the website of the LatinMap.com Foundation. d. The Agreement is subject to the general terms and conditions of the Event Organizer of the Event. If not already in possession of the Customer, the Event Organizer will send the general terms and conditions of the Event Organizer to the Customer upon request.</p><p>3. Formation of Agreement</p><p>a. Unless agreed otherwise in writing, all offers, including advertisements and price lists as mentioned on the website of the LatinMap.com Foundation, are without obligation. b. In offering and selling Tickets, the LatinMap.com Foundation acts as a mediator in the formation of the Agreement between Buyer and Event Organizer. The LatinMap.com Foundation is not a party to the Agreement that is concluded by purchasing a Ticket. The Agreement is concluded after Customer has purchased one or more Tickets via the LatinMap.com Foundation. The LatinMap.com Foundation provides the Tickets on behalf of the Event Organizer.<br/>c. Before confirming his reservation, Customer must always check if the correct Tickets have been reserved. When making the reservation, Customer must always use a correct (e-mail) address. If Customer opts to receive Tickets electronically, the risk of loss, theft, or misuse of the Ticket lies with Customer from the moment the Tickets have reached him. If there is doubt about the accuracy of the data entered by Customer when ordering, the LatinMap.com Foundation may contact Customer using the data entered by Customer. If the LatinMap.com Foundation cannot reach Customer and therefore cannot verify the data, the LatinMap.com Foundation may proceed to cancel the reservation and sell the Tickets to another Customer. If the LatinMap.com Foundation doubts the accuracy or validity of the data entered by Customer at the time of ordering or the payment method used by Customer, it has the right to cancel Customer's reservation and sell the Tickets to another Customer. The LatinMap.com Foundation will endeavor to inform the Customer in such a case. Every Agreement is entered into under the suspensive condition of sufficient availability of the respective products or services.<br/>d. The LatinMap.com Foundation and/or the Event Organizer reserve the right to set a maximum number of Tickets that can be reserved/purchased. If this maximum is exceeded by the Customer, the LatinMap.com Foundation and/or the Event Organizer are entitled to undo the reservation of the Tickets above the maximum number or to invalidate the concerned Tickets.<br/>e. Tickets cannot be returned. The provision in article 6:230p sub e BW applies to tickets.</p><p>4. Prices and Payment</p><p>a. The LatinMap.com Foundation sells Tickets on behalf of Event Organizers who determine the price and the number of available seats. Information regarding price and availability is provided without obligation and subject to change.<br/>b. Customer pays for the products or services ordered via the website, the price mentioned in the Agreement. Payment is made in the manner indicated on the website of the LatinMap.com Foundation. Additional (payment/order) conditions may be applied to the order.</p><p>c. All prices of the LatinMap.com Foundation as mentioned on the website are in euros including VAT, unless expressly stated otherwise.<br/>d. The LatinMap.com Foundation charges service fees for each order. In addition, the LatinMap.com Foundation charges administration fees calculated based on the total amount of the transaction.</p><p>e. If Customer fails to pay the amounts due in time, Customer is, without the need for any demand or notice of default, liable to pay statutory interest on the outstanding amount. If Customer remains negligent in settling the claim after a demand or notice of default, the LatinMap.com Foundation can hand over the claim, in which case Customer is also obliged to reimburse all judicial and extrajudicial costs, including costs calculated by external experts in addition to the legally established costs.</p><p>5. Delivery Times</p><p>a. Tickets are sent to the (e-mail) address provided by Customer at the time of order. The LatinMap.com Foundation will continue to consider the (e-mail) address provided by Customer as correct until Customer notifies the LatinMap.com Foundation of a new (e-mail) address. b. The delivery times stated by the LatinMap.com Foundation will never be considered as a strict deadline unless expressly agreed otherwise. In case of non-timely delivery, the LatinMap.com Foundation must be given written notice of default.</p><p>c. Exceeding any delivery time does not entitle Customer to compensation nor the right to cancel the order or to dissolve the Agreement unless the delivery time has been exceeded to such an extent that it is unreasonable to expect Customer to maintain the Agreement.</p><p>d. With the exception of the option to order electronic Tickets, sales of Tickets via the website and the call center are stopped 8 days before the date of the Event to give the LatinMap.com Foundation the opportunity to still send the Tickets by post.</p><p> 6. Canceled and Rescheduled Events </p><p> a. It is the Customer's responsibility to check whether an Event has been canceled or rescheduled and what the new time or location will be. Although Stichting LatinMap.com will attempt to notify the Customer of the cancellation after receiving the necessary information from the Event Organizer, Stichting LatinMap.com cannot guarantee that the Customer will be notified of the cancellation before the date of the Event. </p><p> b. Stichting LatinMap.com is not responsible for any costs incurred.<br/>c. Tickets for rescheduled Events generally remain valid for the replacement Event. For more information on this, the Customer can contact Stichting LatinMap.com via our email address: mail(at)LatinMap.com. If an Event is moved to another location or date, the Event Organizer may impose conditions on the reimbursement of the ticket price.<br/>d. If an Event is canceled or rescheduled, the Customer may return the Tickets for this Event in accordance with the arrangement determined by the Event Organizer. For the relevant conditions, we refer you to the General Terms and Conditions of the event organizer. If the Event Organizer requests Stichting LatinMap.com to refund the ticket price to the Customer, Stichting LatinMap.com will do so after receiving the relevant funds from the Event Organizer. Service and administration fees are not refunded. </p><p> 7. Tickets </p><p> a. The Tickets distributed by Stichting LatinMap.com remain the property of the Event Organizer. The Customer is not allowed to resell a Ticket if this is in violation of the law. In addition, an Event Organizer may prohibit the resale or commercial use of a Ticket. The Customer should verify this in the general terms and conditions of the organizer, which are referred to on the Ticket (and during the ordering process). In the event of (attempted) resale or commercial use in violation of the law or the conditions of the Event Organizer, the Event Organizer is entitled to invalidate the relevant Ticket and/or impose a fine on the Customer. Holders of invalid access passes will be denied access to the Event, without any right to compensation.<br/>b. Upon receipt, the Customer must check the Tickets, as errors cannot always be corrected. After purchase, Tickets cannot be exchanged or refunded.<br/>c. If the Customer's Ticket is so damaged that its authenticity can no longer be verified, the Customer can contact the online customer service.<br/>d. Special requests, such as reserving wheelchair spaces, must be made known in a timely and explicit manner. Stichting LatinMap.com cannot guarantee that special requests can be accommodated. For more information on this, the Customer can contact Stichting LatinMap.com via our email address: mail(at)LatinMap.com </p><p> 8. Intellectual and Industrial Property Rights </p><p> a. The industrial and intellectual property rights resting on the Ticket from Stichting LatinMap.com belong to Stichting LatinMap.com or its licensor. The Customer shall refrain from any actions that could infringe the rights or other interests of Stichting LatinMap.com or its licensor.<br/>b. The Customer is not allowed to remove or alter any indication of copyright, trademarks, trade names, or other intellectual or industrial property rights from the Ticket, nor to make any changes to the form or any other characteristic of the Ticket.<br/>c. The Customer is prohibited from counterfeiting, duplicating, or reproducing the Ticket in any way.<br/>d. The Agreement does not extend to the transfer of any intellectual property right from Stichting LatinMap.com to the Customer. </p><p> 9. Privacy </p><p> a. The data provided by the Customer when purchasing tickets will be used exclusively by Stichting LatinMap.com to improve its services.<br/>b. The data provided by the Customer when purchasing tickets will never be made available to third parties. </p><p> c. The data provided by the Customer when purchasing tickets will be available to the selling Organization. It is possible that the organization will use the data to send mailings.<br/>d. We expect our clients to handle this information carefully. If this is not the case, we would like to hear about it and will inform the relevant client and, if we deem it necessary, exclude the client from online ticket sales. </p><p> e. Note: we do not accept liability for the privacy policy of our clients. </p><p> 10. Liability </p><p> a. Stichting LatinMap.com cannot be regarded as the organizer (Event Organizer) of the Event and is not responsible for nor guarantees the (artistic) quality and content of the Event and the course of events in or around the Event, and accepts no liability on this basis. </p><p> b. Except for the other liability provisions in these General Terms and Conditions, the total liability of Stichting LatinMap.com due to attributable failure to perform, or due to an unlawful act, is limited to compensation for direct damage up to no more than the amount that has been or can be charged to the Customer for the Tickets, unless the damage is the result of intent and/or gross negligence of </p><p> Stichting LatinMap.com or its subordinates.<br/> c. The liability of Stichting LatinMap.com due to an attributable failure to fulfill a contract only arises in all cases if the Customer promptly and properly declares Stichting LatinMap.com in default in writing, setting a reasonable term for the remediation of the failure, and Stichting LatinMap.com also after that term continues to fail attributably in the fulfillment of its obligations. The notice of default must contain a description of the failure that is as complete and detailed as possible, so that Stichting LatinMap.com can respond adequately.<br/> d. The Customer indemnifies Stichting LatinMap.com against all claims of third parties related to the Customer's failure or insufficient compliance with any obligation towards Stichting LatinMap.com, whether or not arising from the General Terms and Conditions.<br/> e. A condition for the existence of any right to compensation is always that the Customer reports the damage to Stichting LatinMap.com in writing as soon as possible after its occurrence. f. Stichting LatinMap.com can never be held liable for any form of damage to or loss of the Ticket. In case of loss or theft, the Customer cannot claim a replacement Ticket or the refund of any funds. Stichting LatinMap.com is also not liable for delays or for not delivering or incorrectly delivering Tickets, if the delay or non-delivery or incorrect delivery cannot be attributed to Stichting LatinMap.com.<br/> g. Stichting LatinMap.com accepts no liability whatsoever for Tickets obtained from third parties by the Customer. Stichting LatinMap.com cannot guarantee their authenticity either.<br/> h. Stichting LatinMap.com can in no way be held liable for any damage suffered by the holder of this admission ticket due to the actions or omissions of third parties. </p><p> 11. Force Majeure </p><p> a. Force majeure is understood to mean any failure that cannot be attributed to Stichting LatinMap.com, because it is not due to its fault and neither under the law, legal act nor according to generally accepted standards is for its account.<br/> b. Without prejudice to its other rights, Stichting LatinMap.com has the right to suspend the execution of the Customer's order in case of force majeure, by notifying the Customer in writing, and without Stichting LatinMap.com being obliged to any compensation, unless this would be unacceptable according to standards of reasonableness and fairness under the given circumstances. </p><p> 12. Applicable Law and Competent Court </p><p> a. All agreements with Stichting LatinMap.com are governed by Dutch law. </p><p> 13. Miscellaneous </p><p> a. Customers who arrive late at the Event will be admitted - as far as possible - during suitable moments in the Event. However, entry cannot be guaranteed.<br/> b. Where these General Terms and Conditions obligate Stichting LatinMap.com to make a written communication, Stichting LatinMap.com is entitled to make this communication electronically, such as but not limited to an email message or fax.<br/> c. Evident mistakes and typographical errors on the website of Stichting LatinMap.com or in quotations, order confirmations, Tickets, and/or other documents, do not bind Stichting LatinMap.com. d. In case of a disagreement between Stichting LatinMap.com and the Customer regarding the translation and/or interpretation of the General Terms and Conditions, the Dutch version thereof will be decisive.<br/> e. If one or more of the provisions of these General Terms and Conditions or any other agreement with Stichting LatinMap.com should conflict with any applicable legal requirement, the relevant provision will be void and will be replaced by a new legally permissible comparable provision to be determined by Stichting LatinMap.com. </p>",
        },
        'privacy-policy': {
            title: 'Privacy Policy',
            body: '<p>Privacy Policy for LatinMap.com: Discover and Experience the Latin Dance World<br>Last updated: 23-05-2023<br>Thank you for visiting LatinMap.com, the destination for discovering and experiencing the Latin dance world. We highly value the protection of your privacy and aim to be transparent about how we collect, use, share, and protect your personal information. Please read this privacy policy carefully to understand our policies and your rights regarding your personal data.<br>1. Information Collection <br>1.1 Personal Information: When registering on LatinMap.com, we may ask you for personal information such as your name, email address, physical address, and telephone number. This information is provided voluntarily by you.<br>1.2 Automatically Collected Information: We may automatically collect certain information when you visit our website, such as your IP address, browser type, language preferences, access times, and referring website addresses. This information is used to analyze trends, manage our website, and improve the overall user experience.<br>2. Use of Information <br>2.1 Personal Information: We use your personal information to provide you access to the services and functionalities of LatinMap.com. This includes personalizing your experience, sending relevant notifications and updates, and facilitating communication between users (for example, following favorite organizers, DJs, dance schools, and artists).<br>2.2 Marketing Communications: If you agree, we may send you emails related to our services, events, and offers. You can unsubscribe from these communications at any time using the unsubscribe link at the bottom of the emails.<br>2.3 Aggregated Data: We may use aggregated, non-identifiable information for statistical analysis, market research, and other business purposes.<br>3. Information Sharing <br>3.1 Third Parties: We only share your personal information with third parties when necessary to provide the services you have requested, such as event organizers for which you have registered. These third parties are bound by confidentiality obligations and may only use your data to provide the requested services.<br>3.2 Legal Requirements: We may share your personal information if we believe in good faith that disclosure is necessary to comply with a legal obligation, a court order, or a government request.<br>4. Data Security We take reasonable measures to protect your personal information from unauthorized access, use, or disclosure. We employ appropriate physical, technical, and organizational security measures to ensure the confidentiality and integrity of your data. This includes the use of encryption methods, restricted access to your personal information, and regular evaluations of our security practices.<br>5. Data Retention Period We retain your personal information only as long as necessary to fulfill the purposes for which the information was collected, unless a longer retention period is legally required or permitted.<br>6. Cookies and Similar Technologies We may use cookies and similar technologies to collect information and improve your experience on LatinMap.com. You can adjust your browser settings to manage or block the use of cookies, but please note that some features of our website may not function properly if cookies are disabled.<br>7. External Links Our website may contain links to other third-party websites. We have no control over these websites and are not responsible for their privacy practices. We encourage you to read the privacy policy of each website you visit.<br>8. Your Rights You have the right to request access to your personal information that we hold and to correct any inaccuracies. You may also request the deletion of your personal information, unless we are legally required to retain it. Please contact us using the contact details below to exercise these rights.<br>9. Changes to the Privacy Policy We may update this privacy policy from time to time. Any changes will be posted on this page, and the revised version of the privacy policy will indicate the effective date at the top.<br>10. Contact Us If you have any questions about this privacy policy or how we collect, use, or share your personal information, you can contact us via [contact details].<br>Thank you for reading our privacy policy. We are committed to protecting your privacy and providing you with a safe and satisfying user experience on LatinMap.com.</p>',
        },
        about: {
            'about-latin-scene': {
                title: 'About Latin Scene',
                body: 'The Latin dance scene is a vibrant and diverse community that celebrates the rich cultural heritage of Latin America through music, dance, and art. From the lively rhythms of Salsa and Bachata to the sensual movements of Kizomba and Zouk, the Latin dance scene offers a wide range of styles and experiences for dancers of all levels. Whether you are a seasoned dancer looking to refine your technique or a beginner eager to learn the basics, the Latin dance scene has something for everyone. With its welcoming and inclusive atmosphere, the Latin dance scene is a place where people from all walks of life can come together to share their love of dance and music. Discover the magic of the Latin dance scene and join us on the dance floor!',
            },
            salsa: {
                title: 'Salsa',
                body: 'Salsa is a popular form of social dance that originated in the Caribbean. The movements of salsa have its origins in Cuban Son, Cha-cha-cha, Mambo, Puerto Rican Bomba and Plena, and other dance forms. The dance, along with salsa music, originated in the mid-1970s in New York.',
            },
            bachata: {
                title: 'Bachata',
                body: "Bachata is a genre of Latin American music that originated in the Dominican Republic in the first half of the 20th century with European, Indigenous, and African musical elements. The dance is a four-step beat achieved with a walking Cuban hip motion, and a unique 'pop' on the fourth beat.",
            },
            zouk: {
                title: 'Zouk',
                body: "Zouk is a fast jump-up carnival beat style of rhythmic music originating from the Caribbean islands of Guadeloupe and Martinique, popularized by the French Antilles Kassav' in the 1980s. Very sensual dance, close to the partner, with a lot of hip movements.",
            },
            kizomba: {
                title: 'Kizomba',
                body: 'Kizomba is a music genre and dance style originating from Angola in the late 1970s and early 1980s. Known for its slow, sensual rhythm and close partner connection, Kizomba emphasizes smooth, flowing movements and intricate footwork. It has grown in popularity globally, blending African rhythms with modern influences.',
            },
            cuban: {
                title: 'Cuban',
                body: "Cuban music and dance are deeply rooted in the island's diverse cultural heritage, blending African, Spanish, and indigenous influences. Known for its lively and rhythmic styles such as Salsa, Rumba, and Son, Cuban dance often features energetic movements, intricate footwork, and a strong connection to the music. It's a vibrant, expressive form of dance celebrated worldwide.",
            },
        },
        'help-center-faq': {
            title: 'Help Center & FAQ',
        },
        'cookie-policy': {
            title: 'Cookie Policy',
        },
        'create-an-event': {
            title: 'How to Create an Event',
        },
        'create-an-brand-or-an-organization': {
            title: 'How to Create a Brand',
        },
        'become-an-ambassador': {
            title: 'Become an ambassador',
        },
        'promote-your-event': {
            title: 'How to Promote Your Event',
        },
        'professional-account-verification': {
            title: 'Professional Account Verification',
        },
        'about-us': {
            title: 'About us',
        },
    },
    email: {
        'not-unique': 'The email has already been taken.',
    },
    leads: {
        title: 'Submit lead',
        description_p1:
            "Are you a LatinMap user and found a dance school, organization, or event that isn't listed yet? Submit it here — your contribution helps us keep the platform complete, accurate, and valuable for dancers worldwide.",
        description_p2:
            'Are you an organizer and want to be listed on LatinMap? Create a Pro Account and add your activities to the platform for free!',
        description_p3:
            'Thank you for helping us grow LatinMap, the platform that connects the global dance community.',
        plural: 'Leads',
        type: {
            event: 'Event',
            organization: 'Organization',
            label: 'Type',
        },
        name: 'Name',
        address: 'Address',
        country: 'Country',
        city: 'City',
        street: 'Street',
        street_number: 'Street number',
        dance_styles: 'Dance styles',
        music_styles: 'Music styles',
        photos: 'Photos',
        category: 'Event type',
        location_name: 'Venue name',
        location_type: {
            singular: 'Location type',
            online: 'Virtual',
            offline: 'In person',
        },
        timezone: 'Timezone',
        date: 'Date',
        not_posted_by_ambassador: 'Verified by organizer',
        undercondition: 'Under condition',
        warning: 'Important change ',
        cancelled: 'Cancelled',
        posted_by_ambassador: 'Posted by LatinMap Ambassador',
        upcoming: 'Upcoming events',
        status_options: {
            active: 'Active',
            canceled: 'Canceled',
            pending: 'Not confirmed yet',
            changed: 'Important change',
            submitted: 'Submitted',
            under_review: 'Under review',
            implemented: 'Implemented',
            declined: 'Declined',
        },
        submit: 'Submit',
        success: 'The lead has been submitted.',
        unavailable: 'Submitting leads is no longer available.',
        filter_type: 'Filter by type',
        view: 'Lead',
        confirm_delete: 'Delete lead',
        delete_confirmation: 'Are you sure you want to delete this lead?',
        deleted_successfully: 'The lead has been deleted successfully.',
        error_loading: 'Error loading leads',
        status_updated: 'The lead status has been updated.',
        error_status: 'Error updating lead status',
    },
    search: {
        allOragnisations: 'View all dance schools',
        allEvents: 'View all events',
        searchresult: 'Search results',
        type: 'Type',
        title: 'Title',
        brand: 'Brand',
        city: 'City',
        country: 'Country',
        address: 'Address',
        startdate: 'Start date',
        favorite: 'Favorite',
    },
    components: {
        picture_input: {
            drag: 'Upload avatar',
            remove: 'Delete avatar',
            change_avatar: 'Change avatar',
        },
        pictureInput: {
            drag: 'Click here to upload picture',
            change: 'Change',
            remove: 'Remove',
            upload: 'Upload',
            tap: 'Tap to upload',
            select: 'Select a file',
            selected: 'Selected',
            fileSize: 'File size',
            fileType: 'File type',
            aspect: 'Aspect ratio',
        },
    },
    passion: {
        title: 'Feel the Rhythm, Join the Movement',
        subtitle:
            'Discover the vibrant world of Latin dance. Connect with dancers, find events near you, and let the music move your soul.',
    },
    payment: {
        received: 'Payment received',
        thank_you:
            'Thank you for your purchase! We are delighted to inform you that your payment has been successfully received. Your tickets have been generated and sent to the email address provided during the checkout process.',
        download_ticket: 'Download ticket(s)',
        download_button: 'Download ticket(s)',
        email_check:
            'Please check your inbox, including the spam folder, for an email from our ticketing system. Should you encounter any issues or have any questions, our dedicated customer support team is here to assist you. We hope you have a fantastic experience at the event and thank you once again for choosing our ticket shop.',
        failed: 'Payment pending',
        failed_message:
            'Unfortunately, we encountered an issue processing your payment. The transaction was either canceled or timed out. Please try again, and if the problem persists, contact our support team for assistance.',
        download_invoice: 'Download invoice',
        download_invoice_button: 'Download invoice',
        invoice_check: 'Please check your inbox for your invoice.',
        thank_you_for_purchase: 'Thank you for purchasing the featured event! You can download the invoice below.',
    },
    financial_center: {
        financial_dashboard: 'My financial dashboard',
        title: 'Financial Center',
        description: 'Manage your transactions, payouts, promotions, and premium features.',
        overview: 'Overview',
        transactions: 'Transactions',
        payoutsettings: 'Payout settings',
        featured_events: 'Featured events',
        ticketsales: 'Ticket sales',
        premium: 'Premium accounts',
    },
    inputfield: {
        select_here: 'Select here',
    },
    tooltip: {
        edit: 'Edit',
        delete: 'Delete',
        view: 'View',
        hide: 'Hide',
        under_construction: 'Under construction',
    },
    // LA-157: where someone stops the automated emails LatinMap sends.
    email_preferences: {
        error: 'Something went wrong. Please try again in a moment.',
        confirm: {
            title: 'Stop these emails?',
            body: 'You currently get these automated emails at {email}.',
            note: 'Messages about your account, like resetting your password, will still be sent.',
            button: 'Stop these emails',
        },
        done: {
            title: 'You are unsubscribed',
            body: 'We will not send automated emails to {email} any more.',
            undo: 'Start receiving them again',
        },
        unknown: {
            title: 'This link no longer works',
            body: 'The link is not valid. Use the link from a more recent email.',
        },
    },
    // LA-194: the public page an organiser reaches through a submission link.
    submit_event: {
        eyebrow: 'Submit an event',
        title: 'Send your event to LatinMap',
        title_brand: 'Submit an event for {brand}',
        lede: 'Fill in what you know. We prepare it and publish it after a quick check.',
        loading: 'One moment…',
        notes: {
            no_account: 'No account needed — this link is enough',
            checked: 'We check every event before it goes live',
            reply: "If anything is unclear, we'll email you",
        },
        sections: {
            event: 'The event',
            where: 'Where',
            details: 'Details',
            more: 'More details (optional)',
            you: 'About you',
        },
        fields: {
            name: 'Event name',
            organizer_name: 'Organiser / brand name',
            organizer_url: 'Organiser website or social link',
            start_date: 'Date',
            start_time: 'Start time',
            end_date: 'End date',
            end_time: 'End time',
            dance_styles: 'Dance styles',
            event_category: 'Event type',
            event_category_placeholder: 'Choose an event type',
            location_name: 'Venue',
            city: 'City',
            country: 'Country',
            country_placeholder: 'Choose a country',
            timezone_placeholder: 'Choose a timezone',
            short_description: 'Short description',
            description: 'Full description',
            photo_remove: 'Remove photo',
            your_name: 'Your name',
            your_email: 'Your email address',
        },
        hints: {
            organizer:
                'Who is putting this event on? A link to their website or Instagram helps us find the right brand faster.',
            end_date: 'For a one-day event this is the same date as the start.',
            contact: 'We only use this to reach you about this event.',
            photos: 'Up to 5 photos. JPG, PNG or WebP, up to 8 MB each.',
            comma_separated: 'Separate names with a comma.',
        },
        errors: {
            name: 'Please fill in the event name.',
            start_date: 'Please fill in the date.',
            end_date: 'Please fill in the end date.',
            event_category: 'Please choose an event type.',
            too_many: 'You have tried too many times. Wait a little and try again.',
            city: 'Please fill in the city.',
            country: 'Please choose a country.',
            timezone: 'Please choose a timezone.',
            organizer_name: 'Please fill in who is organising this event.',
            email: 'Please fill in a valid email address.',
            failed: 'Sending failed. Please try again in a moment.',
            photo_type: 'Please choose a JPG, PNG or WebP.',
            photo_size: 'That photo is too large. Up to 8 MB.',
            photos_required: 'Please add at least one photo.',
            photos_max: 'You can add up to {max} photos \u2014 the rest were not attached.',
        },
        submit: 'Submit event',
        done: {
            title: 'Thank you, we have your event',
            body: "Someone at LatinMap will look at it. You'll get a confirmation by email.",
            again: 'Submit another event',
        },
        invalid: {
            title: 'This link no longer works',
            body: 'The link expired or was withdrawn. Ask whoever sent it for a new one.',
        },
    },
    // LA-180: the screen Brevo returns to after a subscription is
    // confirmed. Top-level rather than under footer, because the same
    // confirmation ends both the footer form and the signup opt-in.
    newsletter: {
        // LA-176: the fixed sign-up landing page behind every QR code,
        // social bio and e-mail signature.
        stay_updated: {
            title: 'Stay close to the scene',
            lede: 'Get the Latin dance events worth clearing your evening for — parties, festivals, workshops and try-outs, plus new features and giveaways from LatinMap.',
            benefits: {
                agenda: 'The agenda, before everyone else',
                festivals: 'Festivals and congresses worth travelling for',
                features: 'New LatinMap features as they land',
                community: 'Giveaways and news from the community',
            },
            form_title: 'Sign up',
            email_label: 'Your e-mail address',
            submit: 'Join the list',
            terms: 'One e-mail at a time, never your address to anyone else. Unsubscribe with one click, from every message.',
            done: {
                title: 'Check your inbox',
                body: 'We have sent you one e-mail. Click the link in it to confirm, and you are on the list.',
                again: 'Sign up another address',
            },
        },
        confirmed: {
            title: 'Subscription confirmed',
            success: 'Your subscription is confirmed. Welcome aboard!',
            body: 'You will now receive the LatinMap newsletter: the best Latin dance events, straight to your inbox. You can unsubscribe from any newsletter with one click.',
            discover_events: 'Discover events',
            back_home: 'Back to home',
        },
    },
    footer: {
        newsletter: {
            title: 'Stay in the loop',
            subtitle: 'Get the best Latin dance events, straight to your inbox.',
            placeholder: 'Your email address',
            button: 'Subscribe',
            subscribing: 'Subscribing…',
            success: "You're subscribed! Check your inbox to confirm.",
            error: 'Something went wrong. Please try again.',
            invalid_email: 'Please enter a valid email address.',
            terms: 'By subscribing you agree to our privacy policy. Unsubscribe anytime.',
        },
        section_1: {
            title: 'Discover LatinMap',
            about: 'About us',
            how_it_works: 'How it works',
            search: 'Search',
            contact: 'Contact',
        },
        section_2: {
            title: 'Support & Community',
            help: 'Helpcenter & FAQ',
            privacy_policy: 'Privacy policy',
            terms_and_conditions: 'Terms and conditions',
            cookie_policy: 'Cookie policy',
            cookie_settings: 'Cookie settings',
        },
        section_3: {
            title: 'For Professionals',
            create_event: 'How to Create an Event',
            create_organization: 'How to Create a Brand',
            become_ambassador: 'Become an ambassador',
            promote_event: 'How to Promote Your event',
            professional_account_verification: 'Professional Account Verification',
        },
        section_4: {
            title: 'Dance & Event Categories',
            all_events: 'All events',
        },
        follow_our_socials: 'Follow us on social media',
    },
};
